// src/components/ShiftAssignmentModal.js - グループ表示対応版（修正完了版）

import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { supabase } from "../supabase";
import { useOrganizationId } from "../contexts/OrganizationContext";
import { TrendingUp, AlertCircle, ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";

/**
 * シフト割り当て結果の確認と調整モーダル
 */
const ShiftAssignmentModal = ({ 
  isOpen, 
  onClose, 
  matrix, 
  conflicts, 
  staffList, 
  dates, 
  onSave, 
  shiftOptions,
  patterns = [],
  maxConsecutiveWorkDaysProp,
  hopes = {},
  originalHopes = {},
  adminHopes = {},
  holidays = [],
  jobTypeFilter = null,
  events = {},
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [conflictResolutions, setConflictResolutions] = useState({});
  const [editableMatrix, setEditableMatrix] = useState({});
  const [staffGroups, setStaffGroups] = useState([]);
  // 特定日の必要人数オーバーライド: { "YYYY-MM-DD": { patternId: count } }
  const [dailyOverridesMap, setDailyOverridesMap] = useState({});
  const [selectedStaffFilter, setSelectedStaffFilter] = useState("all");
  const [selectedDateRange, setSelectedDateRange] = useState("all");
  const [patternDetails, setPatternDetails] = useState({});
  const [shiftColors, setShiftColors] = useState({});
  const [excludedStaff, setExcludedStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmedShifts, setConfirmedShifts] = useState({});
  const [maxConsecutiveWorkDays, setMaxConsecutiveWorkDays] = useState(6);
  // 🔥 前月末から続く連勤を計算するため、当月の前 30 日分のシフトを保持。
  //   { "empId_YYYY-MM-DD": "displayMark" }
  const [priorMonthMatrix, setPriorMonthMatrix] = useState({});

  const [systemSettings, setSystemSettings] = useState({
    maxConsecutiveWorkDays: 6,
    annualTarget: 120,
    monthlyMin: 8,
    monthlyMax: 11
  });
  
  // 🔥 新規追加：グループ表示用のstate
  const [showGroupDetails, setShowGroupDetails] = useState({});
  const [useGroupDisplay, setUseGroupDisplay] = useState(true);
  const [groupSortMethod, setGroupSortMethod] = useState("alphabetical"); // あいうえお順をデフォルトに

  // テスト希望データ生成
  const generateTestHopeData = () => {
    if (Object.keys(originalHopes || {}).length === 0 && Object.keys(adminHopes || {}).length === 0) {
      console.log("⚠️ 希望データが空のため、テスト希望データを生成します");
      
      const testOriginalHopes = {};
      const testAdminHopes = {};
      
      staffList.slice(0, 3).forEach((staff, staffIndex) => {
        dates.slice(0, 7).forEach((date, dateIndex) => {
          const key = `${staff.employeeId}_${date}`;
          
          if (staffIndex === 0) {
            if (dateIndex % 3 === 0) testOriginalHopes[key] = "off";
          } else if (staffIndex === 1) {
            if (dateIndex % 3 === 1) testAdminHopes[key] = "night";
          } else if (staffIndex === 2) {
            if (dateIndex % 3 === 2) testOriginalHopes[key] = "none";
          }
        });
      });
      
      return { testOriginalHopes, testAdminHopes };
    }
    
    return { testOriginalHopes: originalHopes, testAdminHopes: adminHopes };
  };

  const getHopeStatus = (empId, date) => {
    const key = `${empId}_${date}`;
    const { testOriginalHopes, testAdminHopes } = generateTestHopeData();
    
    const hasOriginalHope = testOriginalHopes[key] !== undefined && testOriginalHopes[key] !== "";
    const hasAdminHope = testAdminHopes[key] !== undefined;
    
    if (hasAdminHope) {
      return { type: 'admin', hope: testAdminHopes[key] };
    } else if (hasOriginalHope) {
      return { type: 'original', hope: testOriginalHopes[key] };
    }
    return { type: 'none', hope: '' };
  };

  const getHopeLabel = (hope, patterns = []) => {  // patternsパラメータを追加
    if (hope == null || hope === undefined) return "希望";
    const hopeStr = String(hope).trim();

    // ① shift_patterns の request_group ID / requestGroupLabel / displayMark / id どれかに一致するか確認
    //    （希望グループID として "morning"・"午前" 等が来ても、ここで requestGroupLabel を表示できる）
    const pattern = patterns.find(p =>
      p.requestGroup === hopeStr
      || p.requestGroup === hopeStr.toLowerCase()
      || p.requestGroupLabel === hopeStr
      || p.displayMark === hopeStr
      || p.id === hopeStr
      || (p.requestDisplayMark && p.requestDisplayMark === hopeStr)
    );
    if (pattern) {
      // 希望フォーム上の表示名（requestGroupLabel）があればそれを優先、なければパターン名
      const label = pattern.requestGroupLabel || pattern.name || hopeStr;
      // 既に「希望」が含まれていれば二重付けしない
      return /希望$/.test(label) ? label : `${label}希望`;
    }

    // ② レガシーのセマンティック種別（移行前の古い希望データ用）
    if (hopeStr === "off" || hopeStr === "OFF") return "休み希望";
    if (hopeStr === "night") return "夜勤希望";
    if (hopeStr === "none" || hopeStr === "day") return "日勤希望";
    if (hopeStr === "◯" || hopeStr === "") return "日勤希望";
    if (hopeStr === "/") return "夜勤希望";

    // ③ 最終フォールバック
    return `${hopeStr}希望`;
  };

  const checkHopeMatch = (hope, currentShift, patterns) => {
    if (!hope) return null;
    
    if (hope === "off") {
      return currentShift === "休";
    } else if (hope === "night") {
      console.log(`🔍 夜勤希望チェック: currentShift="${currentShift}"`);
      
      const nightPatterns = patterns.filter(p => 
        !p.isChainOnlyPattern && (
          p.groupName?.includes("夜勤") || 
          p.name?.includes("夜勤") ||
          p.type === "night"
        )
      );
      
      console.log(`  見つかった夜勤パターン:`, nightPatterns.map(p => ({
        name: p.name,
        displayMark: p.displayMark,
        shortName: p.shortName
      })));
      
      const matched = nightPatterns.some(p => 
        currentShift === p.displayMark || 
        currentShift === p.shortName ||
        currentShift === p.id
      );
      
      console.log(`  マッチ結果: ${matched}`);
    return matched;
  } else if (hope === "paid") {
    // paidパターンを検索
    const paidPattern = patterns.find(p => 
      p.configuration?.request_group === "paid" ||
      p.name?.includes("有給")
    );
    const paidMark = paidPattern ? (paidPattern.displayMark || paidPattern.shortName) : "y_";
    
    const matched = currentShift === paidMark;
    console.log(`🔍 paid希望チェック: currentShift="${currentShift}", paidMark="${paidMark}", matched=${matched}`);
    return matched;
  } else if (hope === "none") {
    return currentShift === "" || currentShift === "◯";
  }
  
  return null;
};
  
  // shiftAutoAssign の getConsecutiveContribution と整合させた共通ヘルパー。
  //   - パターン設定 consecutive_contribution が明示されていればそれを使用
  //   - 未設定なら自動判定: request_group ∈ {off, paid, summer} = 0、その他 = 1
  //   - 0 を返したマークは連勤を切る
  const REST_REQUEST_GROUPS_UI = new Set(["off", "paid", "summer"]);
  const buildPatternByMarkUI = (pats) => {
    const patternByMark = {};
    (pats || []).forEach((p) => {
      if (p.displayMark) patternByMark[p.displayMark] = p;
      if (p.shortName && !patternByMark[p.shortName]) patternByMark[p.shortName] = p;
    });
    return patternByMark;
  };
  const getMarkContribution = (mark, pats, patternByMark) => {
    if (!mark || mark === "休") return 0;
    const p = patternByMark[mark] || (pats || []).find((x) => x.displayMark === mark);
    if (!p) return 1;
    const cfg = p.configuration || {};
    const explicit = p.consecutiveContribution ?? cfg.consecutive_contribution;
    if (explicit != null && Number.isFinite(Number(explicit))) return Number(explicit);
    const rg = cfg.request_group || p.requestGroup;
    if (REST_REQUEST_GROUPS_UI.has(rg)) return 0;
    return 1;
  };
  // 🔥 ユーザーが連勤への寄与度を明示設定しているか判定。
  //   明示設定があるなら、それは「最終値」として扱い、isFollowMark で
  //   勝手に 0 に上書きしてはならない（ユーザーの「夜勤明けも1日連勤として
  //   カウントしたい」という意思を尊重する）。
  const hasExplicitContribution = (mark, pats, patternByMark) => {
    if (!mark) return false;
    const p = patternByMark[mark] || (pats || []).find((x) => x.displayMark === mark);
    if (!p) return false;
    const cfg = p.configuration || {};
    const e = p.consecutiveContribution ?? cfg.consecutive_contribution;
    return e != null && Number.isFinite(Number(e));
  };
  // 前日が postShiftIds を持ち、今日がその指定先なら follow（明け）
  //   当月初日 (idx === 0) の場合は前月末セル (priorMatrix) を参照する。
  const isFollowMark = (dates, idx, matrix, staffId, pats, patternByMark, priorMatrix = {}) => {
    const today = matrix[`${staffId}_${dates[idx]}`];
    if (!today) return false;
    let prev;
    if (idx === 0) {
      const prevDate = dayjs(dates[0]).subtract(1, 'day').format('YYYY-MM-DD');
      prev = priorMatrix[`${staffId}_${prevDate}`];
    } else {
      prev = matrix[`${staffId}_${dates[idx - 1]}`];
    }
    if (!prev) return false;
    const prevPattern = patternByMark[prev];
    const prevIds =
      (prevPattern?.configuration?.post_shift_ids) ||
      prevPattern?.postShiftIds || [];
    if (prevIds.length === 0) return false;
    return prevIds.some((ref) => {
      const target = (pats || []).find((q) => q.id === ref) || patternByMark[ref];
      return target?.displayMark === today;
    });
  };

  const calculateMaxConsecutiveDays = (staffId, dates, matrix, pats, priorMatrix = {}) => {
    const patternByMark = buildPatternByMarkUI(pats);
    let maxSum = 0;
    let currentSum = 0;
    let maxStart = -1;
    let maxEnd = -1;
    let currentStart = -1;
    const trace = [];

    // 🔥 前月末から続く連勤を初期 currentSum に積む。
    //   dates[0] の前日から30日遡り、寄与度 0（休み等）に当たったら break。
    //   ただし isFollowMark で前月→前々月の follow 判定は重いので簡易化:
    //   priorMatrix 内の値そのものを getMarkContribution で評価。
    let priorSum = 0;
    if (dates.length > 0) {
      let d = dayjs(dates[0]).subtract(1, 'day');
      for (let i = 0; i < 30; i++) {
        const ds = d.format('YYYY-MM-DD');
        const shift = priorMatrix[`${staffId}_${ds}`];
        if (!shift) break;
        // 前月分: isFollowMark の正確な判定は重いので、寄与度のみで判定。
        // 明示寄与度の有無に関係なく、その値自体（>0 なら勤務扱い）で連勤継続
        const contrib = getMarkContribution(shift, pats, patternByMark);
        if (contrib <= 0) break;
        priorSum += contrib;
        d = d.subtract(1, 'day');
      }
    }
    if (priorSum > 0) {
      currentSum = priorSum;
      currentStart = -1; // 前月から続いていることを示すマーカー
      if (currentSum > maxSum) {
        maxSum = currentSum;
        maxStart = -1;
        maxEnd = -1;
      }
      trace.push(`(前月から ${priorSum})`);
    }

    dates.forEach((date, index) => {
      const key = `${staffId}_${date}`;
      const shift = matrix[key];
      if (!shift) {
        if (currentSum > 0) trace.push(`(空欄→切)`);
        currentSum = 0;
        return;
      }
      // 🔥 ユーザーが寄与度を明示設定したマークは、follow 判定で 0 上書きしない。
      //   例: 「夜勤明け」マークに寄与度=1 を設定 → 12連勤を期待。
      //       isFollowMark で 0 にすると、明けで連勤が切れて 4.5 とかになる。
      const contribRaw = getMarkContribution(shift, pats, patternByMark);
      const explicit = hasExplicitContribution(shift, pats, patternByMark);
      const follow = !explicit && isFollowMark(dates, index, matrix, staffId, pats, patternByMark, priorMatrix);
      const contrib = follow ? 0 : contribRaw;
      trace.push(`${shift}:${follow ? 'F' : contrib}${explicit ? '!' : ''}`);
      if (contrib <= 0) {
        currentSum = 0;
        return;
      }
      if (currentSum === 0) currentStart = index;
      currentSum += contrib;
      if (currentSum > maxSum) {
        maxSum = currentSum;
        maxStart = currentStart;
        maxEnd = index;
      }
    });

    // 8連勤想定なのに表示が違うケースを追える「全員分1行ログ」（重い場合は外す）
    if (typeof window !== 'undefined' && window.__SHIFT_DEBUG_CONSEC) {
      console.log(`[連勤計算] ${staffId} max=${maxSum.toFixed(1)} trace=[${trace.join(', ')}]`);
    }

    return {
      max: maxSum,
      start: maxStart,
      end: maxEnd,
    };
  };
  
  // 🔥 修正版：パターンをグループ化する関数（重複解消）
  const groupPatternsByGroupName = (patterns, matrix, staffIds, dates) => {
    const groups = {};
    const patternCounts = {};
    
    // まず各パターンの使用回数を計算
    patterns.forEach(pattern => {
      const mark = pattern.displayMark || pattern.id;
      patternCounts[mark] = 0;
      
      dates.forEach(date => {
        staffIds.forEach(staffId => {
          const value = matrix[`${staffId}_${date}`];
          const normalizedValue = value === "" ? "◯" : value;
          if (normalizedValue === mark || (mark === "◯" && value === "")) {
            patternCounts[mark]++;
          }
        });
      });
    });
    
    // パターンをグループ化
    let hasDayShiftPattern = false; // 日勤パターンが既にグループに所属しているかチェック
    let dayShiftGroupName = null; // 既存の日勤グループ名を記録
    let individualDayShiftGroupName = null; // 個別の日勤グループ名を記録
    
    patterns.forEach(pattern => {
      const mark = pattern.displayMark || pattern.id;
      let groupName = pattern.groupName || `個別_${pattern.name}`;
      
      // 日勤系パターンの特別処理
      // ⚠️ mark === "" の判定は Phase F で廃止（空文字は「未定」であり日勤ではない）。
      //    「◯」マーク or パターン名に「日勤」を含むものだけを日勤系扱い。
      const isDayShiftPattern = (mark === "◯" || pattern.name?.includes("日勤"));
      
      if (isDayShiftPattern) {
        if (pattern.groupName) {
          // グループ名が設定されている日勤パターン
          hasDayShiftPattern = true;
          dayShiftGroupName = pattern.groupName;
          console.log(`既存の日勤パターンを発見: ${pattern.name} (グループ: ${pattern.groupName})`);
        } else {
          // グループ名が設定されていない日勤パターン → 「日勤系」に統合
          groupName = "日勤系";
          individualDayShiftGroupName = "日勤系";
          console.log(`個別日勤パターンを日勤系に統合: ${pattern.name}`);
        }
      }
      
      if (!groups[groupName]) {
        groups[groupName] = {
          name: groupName,
          patterns: [],
          totalCount: 0,
          color: getGroupColor(groupName, pattern),
          isIndividual: !pattern.groupName && groupName !== "日勤系"
        };
      }
      
      groups[groupName].patterns.push({
        ...pattern,
        mark: mark,
        count: patternCounts[mark] || 0
      });
      groups[groupName].totalCount += patternCounts[mark] || 0;
    });
    
    // ⚠️ 旧仕様の「空文字セル = 日勤」集計は Phase F で廃止。
    //    空文字／NULL は「未定」であり、勤務として集計しない。
    //    明示マーク（"◯" など）が入ったセルだけが各パターングループにカウントされる。
    //    "◯" マークは patternCounts 経由で通常の日勤パターン集計に乗るため、
    //    ここで追加集計する必要はない。
    const dayShiftCount = 0;
    
    console.log("🔍 グループ化結果:", {
      totalGroups: Object.keys(groups).length,
      groupNames: Object.keys(groups),
      hasDayShiftPattern,
      dayShiftGroupName,
      individualDayShiftGroupName,
      dayShiftCount
    });
    
    return groups;
  };
  
  // グループの色を決定する関数
  const getGroupColor = (groupName, samplePattern) => {
    const colorMap = {
      "日勤系": "#e3f2fd",
      "夜勤系": "#fce4ec", 
      "オンコール系": "#fff3e0",
      "遅番系": "#e8f5e9",
      "休日系": "#f1f5f9"
    };
    
    // グループ名での完全一致
    if (colorMap[groupName]) return colorMap[groupName];
    
    // 部分一致
    for (const [key, color] of Object.entries(colorMap)) {
      if (groupName.includes(key.replace("系", ""))) {
        return color;
      }
    }
    
    // パターンの種類に基づく色
    if (samplePattern) {
      if (samplePattern.name?.includes("夜勤")) return "#fce4ec";
      if (samplePattern.name?.includes("日勤")) return "#e3f2fd";
      if (samplePattern.name?.includes("オンコール")) return "#fff3e0";
      if (samplePattern.name?.includes("遅")) return "#e8f5e9";
      if (samplePattern.displayMark === "休") return "#f1f5f9";
    }
    
    // デフォルト色
    return "#f9fafb";
  };

  // 勤務パターンの色を自動生成
  const generatePatternColors = (patternList) => {
    const colors = {
      "": "#e3f2fd",
      "◯": "#e3f2fd",
      "休": "#e8f5e9",
      "/": "#fff3e0",
    };
    
    try {
      patternList.forEach((pattern) => {
        const mark = pattern.displayMark || pattern.id;
        if (!colors[mark]) {
          if (pattern.groupName?.includes("日勤")) {
            colors[mark] = `hsl(210, ${70 + Math.random() * 20}%, 95%)`;
          } else if (pattern.groupName?.includes("夜勤")) {
            colors[mark] = `hsl(30, ${70 + Math.random() * 20}%, 95%)`;
          } else if (pattern.groupName?.includes("オンコール")) {
            colors[mark] = `hsl(280, ${70 + Math.random() * 20}%, 95%)`;
          } else if (pattern.groupName?.includes("休")) {
            colors[mark] = `hsl(120, ${70 + Math.random() * 20}%, 95%)`;
          } else {
            const hue = Math.floor(Math.random() * 360);
            colors[mark] = `hsl(${hue}, 70%, 95%)`;
          }
        }
      });
    } catch (error) {
      console.error("パターン色生成エラー:", error);
    }
    
    return colors;
  };

  // スタイル定義
  const styles = {
    modalBackdrop: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      overflowY: "auto",
      padding: "2rem 0",
      zIndex: 9500
    },
    modalContent: {
      backgroundColor: "white",
      padding: "2rem",
      borderRadius: "8px",
      width: "95%",
      maxWidth: "1400px",
      maxHeight: "85vh",
      overflow: "auto",
      position: "relative"
    },
    modalHeader: {
      borderBottom: "1px solid #eee",
      paddingBottom: "1rem",
      marginBottom: "1.5rem",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: "1.5rem",
      cursor: "pointer",
      color: "#777"
    },
    tabNav: {
      display: "flex",
      marginBottom: "1.5rem",
      borderBottom: "1px solid #eee",
      overflowX: "auto",
      padding: "0.5rem 0"
    },
    tabButton: {
      padding: "0.75rem 1.5rem",
      marginRight: "0.5rem",
      border: "none",
      borderRadius: "4px 4px 0 0",
      cursor: "pointer",
      backgroundColor: "#f5f5f5",
      fontWeight: "500"
    },
    tabButtonActive: {
      backgroundColor: "#007bff",
      color: "white"
    },
    statsCard: {
      border: "1px solid #eee",
      borderRadius: "8px",
      padding: "1.5rem",
      marginBottom: "1.5rem"
    },
    statGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
      gap: "1rem",
      marginTop: "1rem"
    },
    statItem: {
      backgroundColor: "#f5f5f5",
      borderRadius: "4px",
      padding: "1rem",
      textAlign: "center"
    },
    statValue: {
      fontSize: "1.5rem",
      fontWeight: "bold",
      color: "#007bff",
      marginBottom: "0.5rem"
    },
    saveButton: {
      backgroundColor: "#28a745",
      color: "white",
      border: "none",
      padding: "0.75rem 1.5rem",
      borderRadius: "4px",
      cursor: "pointer",
      marginTop: "1.5rem",
      fontSize: "1rem",
      fontWeight: "500"
    },
    // 🔥 新規追加：グループ表示用スタイル
    groupRow: {
      backgroundColor: "#f8f9fa",
      borderLeft: "4px solid",
      fontWeight: "600",
      border: "1px solid #e2e8f0"
    },
    detailRow: {
      backgroundColor: "#fafbfc",
      opacity: 0.9,
      border: "1px solid #e2e8f0"
    },
    toggleButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      fontSize: "12px",
      color: "#6c757d",
      padding: "4px 8px",
      borderRadius: "4px",
    },
    groupHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '700',
      fontSize: '13px',
      color: '#1f2937',
      whiteSpace: 'nowrap'
    },
    groupToggle: {
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "4px",
      borderRadius: "4px",
      display: "flex",
      alignItems: "center",
      color: "#6c757d",
    }
  };

  // コンポーネント初期化
  useEffect(() => {
    // 🔥 ここに追加（最初に）
  console.log("🔍 モーダルに渡された matrix のサンプル10件:");
  console.log(Object.entries(matrix || {}).slice(0, 10));
  
  console.log("🔍 matrix の値ごとの集計:");
  const valueCounts = {};
  Object.values(matrix || {}).forEach(value => {
    const key = value === null ? "null" : value === "" ? "空文字" : value;
    valueCounts[key] = (valueCounts[key] || 0) + 1;
  });
  console.log(valueCounts);
  // 🔥 ここまで追加
    const fetchData = async () => {
      try {
        // organizationIdを取得
        const organizationId = localStorage.getItem('organizationId');
        
        if (!organizationId) {
          console.error("❌ organizationId が設定されていません");
          setLoading(false);  // ✅ 既にある
          alert("組織情報が取得できませんでした。再度ログインしてください。");
          return;
        }
        
        console.log(`✅ 組織ID: ${organizationId}`);
  
        // システム設定取得
        const fetchSystemSettings = async () => {
          const { data: policyData, error } = await supabase
            .from('holiday_policies')
            .select('*')
            .eq('organization_id', organizationId)
            .single();
  
          if (error) {
            console.error("システム設定取得エラー:", error);
            return {
              maxConsecutiveWorkDays: 6,
              annualTarget: 120,
              monthlyMin: 8,
              monthlyMax: 11
            };
          }
  
          return {
            maxConsecutiveWorkDays: policyData.max_consecutive_work_days || 6,
            annualTarget: policyData.annual_target || 120,
            monthlyMin: policyData.monthly_min || 8,
            monthlyMax: policyData.monthly_max || 11
          };
        };
  
        // スキルグループ取得
        const fetchStaffGroups = async () => {
          const { data: skillGroups, error } = await supabase
            .from('skill_groups')
            .select('*')
            .eq('organization_id', organizationId);
  
          if (error) {
            console.error("スキルグループ取得エラー:", error);
            return [];
          }
  
          console.log(`✅ ${skillGroups?.length || 0}件のスキルグループを取得`);
          return skillGroups || [];
        };
  
        const [settings, groups] = await Promise.all([
          fetchSystemSettings(),
          fetchStaffGroups()
        ]);
  
        setSystemSettings(settings);
        // ⚠️ 単体 state の maxConsecutiveWorkDays も同期。これがないと初期値 6 のまま固定される。
        if (settings?.maxConsecutiveWorkDays != null) {
          setMaxConsecutiveWorkDays(Number(settings.maxConsecutiveWorkDays));
        }
        setStaffGroups(groups);
        
        // 🔥 重要：ローディングを解除！
        setLoading(false);
        
      } catch (error) {
        console.error("データ取得エラー:", error);
        setLoading(false);  // ✅ 既にある
      }
    };
  
    fetchData();
  }, []); // 依存配列にcurrentOrganizationを追加

  // 🔥 ここに新しい useEffect として追加
  // ShiftAssignmentModal.js の行605付近（editableMatrix初期化の直後）
  useEffect(() => {
    if (matrix && Object.keys(matrix).length > 0) {
      setEditableMatrix(matrix);
    }
  }, [matrix]);

  // 🔥 当月の前 30 日分のシフトを fetch して、月またぎの連勤計算に使う。
  //   これがないと「6/1 が連勤の途中」のケースで連勤が過小評価され、
  //   違反バッジが出るべきところで出ない（または見落とす）原因になる。
  useEffect(() => {
    if (!isOpen || !dates?.length || !staffList?.length) return;
    const firstDate = dates[0];
    if (!firstDate) return;
    const fromDate = dayjs(firstDate).subtract(30, 'day').format('YYYY-MM-DD');
    const toDate = dayjs(firstDate).subtract(1, 'day').format('YYYY-MM-DD');
    const empIds = staffList.map((s) => s.employeeId).filter(Boolean);
    if (!empIds.length) return;

    const fetchPriorShifts = async () => {
      try {
        const { data, error } = await supabase
          .from('daily_shifts')
          .select('employee_id, shift_date, shift_type')
          .in('employee_id', empIds)
          .gte('shift_date', fromDate)
          .lte('shift_date', toDate);
        if (error) {
          console.warn('前月シフト取得失敗:', error);
          return;
        }
        const m = {};
        (data || []).forEach((r) => {
          m[`${r.employee_id}_${r.shift_date}`] = r.shift_type || '';
        });
        setPriorMonthMatrix(m);
        console.log(`🔍 前月セル取得: ${Object.keys(m).length}件 (${fromDate}〜${toDate})`);
      } catch (e) {
        console.warn('前月シフト取得 例外:', e);
      }
    };
    fetchPriorShifts();
  }, [isOpen, dates, staffList]);

  // 特定日の必要人数オーバーライドを取得
  useEffect(() => {
    const loadOverrides = async () => {
      try {
        const organizationId = localStorage.getItem('organizationId');
        if (!organizationId || !dates || dates.length === 0) return;
        const first = dates[0];
        const last = dates[dates.length - 1];
        const { data, error } = await supabase
          .from("daily_required_overrides")
          .select("override_date, required_counts")
          .eq("organization_id", organizationId)
          .gte("override_date", first)
          .lte("override_date", last);
        if (error) {
          console.warn("daily_required_overrides:", error);
          return;
        }
        const map = {};
        (data || []).forEach((row) => {
          map[row.override_date] = row.required_counts || {};
        });
        setDailyOverridesMap(map);
      } catch (e) {
        console.warn("オーバーライド取得失敗:", e);
      }
    };
    if (isOpen) loadOverrides();
  }, [isOpen, dates]);

  // 競合の解決を記録
  const handleResolveConflict = (key, resolution) => {
    setConflictResolutions({
      ...conflictResolutions,
      [key]: resolution
    });
  };

  // シフト変更ハンドラ
  const handleShiftChange = (empId, date, value) => {
    const key = `${empId}_${date}`;
    setEditableMatrix({
      ...editableMatrix,
      [key]: value
    });
  };

  // 保存ハンドラ
  const handleSave = () => {
    try {
      const finalMatrix = {
        ...editableMatrix,
        _confirmed: confirmedShifts
      };
      
      onSave(finalMatrix, conflictResolutions);
      onClose();
    } catch (error) {
      console.error("シフト保存エラー:", error);
      alert("変更の保存に失敗しました: " + error.message);
    }
  };

  // シフト統計の計算
  const calculateShiftStats = () => {
    try {
      const uniqueShiftTypes = new Set();
      
      Object.values(editableMatrix).forEach(value => {
        if (value) uniqueShiftTypes.add(value);
      });
      
      const stats = {
        total: 0,
        byPattern: {},
        autoAssignedTotal: 0,
        excludedTotal: excludedStaff.length * dates.length
      };
      
      uniqueShiftTypes.add("◯");
      
      uniqueShiftTypes.forEach(type => {
        stats.byPattern[type] = {
          count: 0,
          percentage: 0,
          label: getPatternLabel(type) || type
        };
      });
      
      const includedStaffIds = staffList
        .filter(s => !s.excludeFromAutoAssign)
        .map(s => s.employeeId);
      
      Object.entries(editableMatrix).forEach(([key, value]) => {
        const [empId, date] = key.split('_');
        
        const isAutoAssigned = includedStaffIds.includes(empId);
        
        stats.total++;
        if (isAutoAssigned) {
          stats.autoAssignedTotal++;
        }
        
        const shiftType = (!value || value === "") ? "◯" : value;
        
        stats.byPattern[shiftType].count++;
      });
      
      uniqueShiftTypes.forEach(type => {
        if (stats.total > 0) {
          const percentage = (stats.byPattern[type].count / stats.total) * 100;
          stats.byPattern[type].percentage = Math.round(percentage);
        }
      });
      
      return stats;
    } catch (error) {
      console.error("シフト統計計算エラー:", error);
      return {
        total: 0,
        byPattern: {},
        autoAssignedTotal: 0,
        excludedTotal: 0
      };
    }
  };
  
  const getPatternLabel = (patternMark) => {
    try {
      const pattern = patternDetails[patternMark];
      if (!pattern) {
        if (patternMark === "" || patternMark === "◯") return "日勤";
        if (patternMark === "休") return "休日";
        if (patternMark === "/") return "夜勤";
        return patternMark;
      }
      return pattern.name;
    } catch (error) {
      console.error(`パターンラベル取得エラー: ${patternMark}`, error);
      return patternMark;
    }
  };

  // 希望反映率を計算する関数
  // 希望反映率を計算する関数
const calculateHopeReflectionRate = () => {
  try {
    const stats = {
      total: { count: 0, reflected: 0, rate: 0 },
      byType: {},  // 動的に希望タイプを格納
      admin: { count: 0, reflected: 0, rate: 0 }
    };

    const { testOriginalHopes, testAdminHopes } = generateTestHopeData();
    // 🔥 職種フィルタが指定されている場合は、その職種のスタッフだけを分母に。
    //   これがないと「職種別の自動割り当て」を実行しても、概要の希望反映率
    //   分母に他職種の希望まで含まれ、職種別 100% でも全体 < 100% に見える。
    const targetStaffList = jobTypeFilter
      ? staffList.filter(
          (s) => String(s.job_type_id || s.jobTypeId || "") === String(jobTypeFilter)
        )
      : staffList;
    const allStaffIds = targetStaffList.map(s => s.employeeId);

    // ⚙️ デバッグ: モーダルに渡された希望データの件数とサンプル
    console.log('🏷️ ShiftAssignmentModal version: 2026-06-15-r5 (job filter aware)');
    console.log('🔍 希望反映率 対象スタッフ:', {
      jobTypeFilter,
      targetStaff_count: targetStaffList.length,
      totalStaff_count: staffList.length,
    });
    console.log('🔍 受け取った希望データ:', {
      originalHopes_count: Object.keys(originalHopes || {}).length,
      adminHopes_count: Object.keys(adminHopes || {}).length,
      testOriginalHopes_count: Object.keys(testOriginalHopes || {}).length,
      testAdminHopes_count: Object.keys(testAdminHopes || {}).length,
      adminHopes_sample: Object.entries(adminHopes || {}).slice(0, 5),
      originalHopes_sample: Object.entries(originalHopes || {}).slice(0, 5),
    });
    
    // まず全ての希望タイプを収集
    const hopeTypes = new Set();
    allStaffIds.forEach(empId => {
      const staff = staffList.find(s => s.employeeId === empId);
      if (staff?.excludeFromAutoAssign) return;
      
      dates.forEach(date => {
        const hopeStatus = getHopeStatus(empId, date);
        if (hopeStatus.hope && hopeStatus.hope !== '') {
          hopeTypes.add(hopeStatus.hope);
        }
      });
    });
    
    // 各希望タイプの統計を初期化
    hopeTypes.forEach(type => {
      stats.byType[type] = { 
        count: 0, 
        reflected: 0, 
        rate: 0,
        label: getHopeLabel(type, patterns)
      };
    });
    
    // 実際の集計処理
    allStaffIds.forEach(empId => {
      const staff = staffList.find(s => s.employeeId === empId);
      if (staff?.excludeFromAutoAssign) return;
      
      // editableMatrix は useEffect で初期化されるため、初回 render では空。
      // その時点で計算が走ると currentShift が全て "" になり反映率が 0% になる。
      // → editableMatrix が空のときは matrix プロップを直接参照する。
      const sourceMatrix =
        editableMatrix && Object.keys(editableMatrix).length > 0
          ? editableMatrix
          : (matrix || {});

      dates.forEach(date => {
        const key = `${empId}_${date}`;
        const hopeStatus = getHopeStatus(empId, date);
        const hope = hopeStatus.hope;
        const hopeType = hopeStatus.type;

        if (hope && hope !== '') {
          const currentShift = sourceMatrix[key] === null ? null : (sourceMatrix[key] || "");
          
          stats.total.count++;
          if (hopeType === 'admin') {
            stats.admin.count++;
          }
          
          if (stats.byType[hope]) {
            stats.byType[hope].count++;
          }
          
          // 希望が反映されているかチェック
          //   shiftAutoAssign の translateHopeToMark と整合する形で、希望に対応する
          //   「期待されるマーク群」を求めて currentShift と照合する。
          const expectedMarks = (() => {
            const marks = new Set();
            if (!hope) return marks;
            const v = String(hope).trim();
            if (!v) return marks;
            const lower = v.toLowerCase();
            marks.add(v); // 直接マークが届くケース

            // off
            //   "休み希望" は完全休み系のどれに変換されても反映扱いにする：
            //   request_group ∈ {off, paid, summer} のパターンの displayMark を全部許容。
            if (lower === "off" || lower === "休" || v === "OFF") {
              marks.add("休");
              patterns.forEach((p) => {
                const cfg = p.configuration || {};
                const rg = cfg.request_group || p.requestGroup;
                if (rg === "off" || rg === "paid" || rg === "summer") {
                  if (p.displayMark) marks.add(p.displayMark);
                  if (p.shortName) marks.add(p.shortName);
                }
              });
            }

            // day / none / ◯ / ○ / "": 日勤系候補
            if (lower === "none" || lower === "day" || lower === "◯" || v === "○" || v === "") {
              marks.add("◯");
              patterns.forEach((p) => {
                const cfg = p.configuration || {};
                const rg = cfg.request_group || p.requestGroup;
                const ame =
                  cfg.assignment_mode_enabled === true ||
                  cfg.assignmentModeEnabled === true ||
                  p.assignmentModeEnabled === true;
                const isDay =
                  rg === "day" ||
                  ame ||
                  (p.name?.includes("日勤") && !p.name?.includes("夜勤"));
                if (isDay) {
                  if (p.displayMark) marks.add(p.displayMark);
                  if (p.shortName) marks.add(p.shortName);
                }
              });
            }

            // night
            if (lower === "night" || v === "/") {
              patterns.forEach((p) => {
                const cfg = p.configuration || {};
                const rg = cfg.request_group || p.requestGroup;
                const isNight =
                  rg === "night" ||
                  p.groupName?.includes("夜勤") ||
                  cfg.group_name?.includes("夜勤") ||
                  p.name?.includes("夜勤") ||
                  (p.displayMark && p.displayMark.startsWith("/"));
                const chainOnly = p.isChainOnlyPattern || cfg.is_chain_only_pattern;
                if (isNight && !chainOnly) {
                  if (p.displayMark) marks.add(p.displayMark);
                  if (p.shortName) marks.add(p.shortName);
                }
              });
              marks.add("/");
            }

            // 汎用: request_group / requestGroupLabel 一致のパターン
            //   "paid" / "half" / "summer" / "morning" などはここで解決される
            patterns.forEach((p) => {
              const cfg = p.configuration || {};
              const rg = cfg.request_group || p.requestGroup;
              const rgl = cfg.request_group_label || p.requestGroupLabel;
              if (rg === v || rg === lower || rgl === v || rgl === lower) {
                if (p.displayMark) marks.add(p.displayMark);
                if (p.shortName) marks.add(p.shortName);
              }
            });

            return marks;
          })();
          const isReflected = expectedMarks.has(currentShift);
          if (!isReflected && (hope === "day" || hope === "summer" || hope === "half" || hope === "paid")) {
            console.log(
              `🔍 【反映率計算】${hope} 不一致: currentShift="${currentShift}", expected=`,
              Array.from(expectedMarks)
            );
          }
          if (isReflected) {
            stats.total.reflected++;
            if (hopeType === 'admin') {
              stats.admin.reflected++;
            }
            if (stats.byType[hope]) {
              stats.byType[hope].reflected++;
            }
          }
        }
      });
    });
    
    // 反映率を計算
    if (stats.total.count > 0) {
      stats.total.rate = Math.round((stats.total.reflected / stats.total.count) * 100);
    }
    if (stats.admin.count > 0) {
      stats.admin.rate = Math.round((stats.admin.reflected / stats.admin.count) * 100);
    }
    
    Object.keys(stats.byType).forEach(key => {
      if (stats.byType[key].count > 0) {
        stats.byType[key].rate = Math.round((stats.byType[key].reflected / stats.byType[key].count) * 100);
      }
    });
    
    console.log("🔍 希望反映率計算結果:", stats);
    
    return stats;
  } catch (error) {
    console.error("希望反映率計算エラー:", error);
    return {
      total: { count: 0, reflected: 0, rate: 0 },
      byType: {},
      admin: { count: 0, reflected: 0, rate: 0 }
    };
  }
};
      
     

  // 指定日のパターン必要人数（祝日対応含む）を計算
  // ベース計算（chain follow 加算なし）
  const _getBaseRequiredCountForPattern = (pattern, date) => {
    if (!pattern) return 0;
    if (dailyOverridesMap && dailyOverridesMap[date]) {
      const ov = dailyOverridesMap[date];
      if (pattern.id && ov[pattern.id] != null) return Number(ov[pattern.id]);
    }
    const dayNames = ["日", "月", "火", "水", "木", "金", "土"];
    const wd = dayNames[dayjs(date).day()];
    const reqMap = pattern.requiredByDayOfWeek || pattern.required_by_day_of_week || pattern.requiredCount || pattern.required_count || pattern.configuration?.required_by_day_of_week || {};
    const base = Number(reqMap[wd] || 0);
    if (base <= 0) return 0;
    const dow = dayjs(date).day();
    const isHol = (holidays || []).includes(date) || dow === 0 || dow === 6;
    if (!isHol) return base;
    const adj = pattern.holidayAdjustment || pattern.configuration?.holiday_adjustment || "default";
    if (adj === "no_assign") return 0;
    if (adj === "reduce_count") return Math.max(0, Math.round(base * 0.7));
    if (adj === "increase_count") return Math.round(base * 1.3);
    if (adj === "custom_count") {
      return Number(pattern.holidayCustomCount || pattern.configuration?.holiday_custom_count || 0);
    }
    return base;
  };

  // 🔥 chain follow への自動加算込みの required:
  //   - 連鎖専用パターン (isChainOnlyPattern=true): 前日 chain start の required 合計
  //   - 通常 follow パターン: 手入力 + override 増分
  const getRequiredCountForPattern = (pattern, date) => {
    if (!pattern?.id) return _getBaseRequiredCountForPattern(pattern, date);
    const prevDate = dayjs(date).subtract(1, 'day').format('YYYY-MM-DD');

    // 🔥 連鎖専用パターンは完全動的: 前日 chain start の required 合計
    const isChainOnly = pattern.isChainOnlyPattern || pattern.is_chain_only_pattern
      || pattern.configuration?.is_chain_only_pattern;
    if (isChainOnly) {
      let total = 0;
      (patterns || []).forEach((startPat) => {
        const list = startPat.postShiftIds || startPat.post_shift_ids || startPat.configuration?.post_shift_ids || [];
        if (!list.includes(pattern.id)) return;
        total += _getBaseRequiredCountForPattern(startPat, prevDate);
      });
      return total;
    }

    // 通常 follow: ベース + override 増分のみ加算
    const base = _getBaseRequiredCountForPattern(pattern, date);
    let addend = 0;
    (patterns || []).forEach((startPat) => {
      const list = startPat.postShiftIds || startPat.post_shift_ids || startPat.configuration?.post_shift_ids || [];
      if (!list.includes(pattern.id)) return;
      const startActual = _getBaseRequiredCountForPattern(startPat, prevDate);
      const overrideForStart = dailyOverridesMap?.[prevDate]?.[startPat.id];
      if (overrideForStart != null) {
        const dayNames = ["日", "月", "火", "水", "木", "金", "土"];
        const wd = dayNames[dayjs(prevDate).day()];
        const startReqMap = startPat.requiredByDayOfWeek || startPat.required_by_day_of_week || startPat.requiredCount || startPat.required_count || startPat.configuration?.required_by_day_of_week || {};
        const startDefault = Number(startReqMap[wd] || 0);
        const delta = startActual - startDefault;
        if (delta > 0) addend += delta;
      }
    });
    return base + addend;
  };

  const isFixedPattern = (pattern) => {
    if (!pattern) return false;
    return (pattern.flexibilityType || pattern.configuration?.flexibility_type) === "fixed";
  };

  // 🔥 パターンの「必要人数カウント対象外グループ」に属するスタッフ ID セットを返す
  const getExcludedStaffIdsForPattern = (pattern) => {
    const groups = pattern?.excludedCountGroups
      || pattern?.excluded_count_groups
      || pattern?.configuration?.excluded_count_groups
      || [];
    if (!groups.length) return new Set();
    const ids = new Set();
    groups.forEach((groupName) => {
      const grp = staffGroups.find((g) => g.name === groupName);
      if (grp && Array.isArray(grp.members)) {
        grp.members.forEach((m) => ids.add(String(m)));
      }
    });
    // デバッグ: 一度だけ出力（パターン名 + 除外グループ + 抽出された ID 数）
    if (!window.__excludedDebugLogged) window.__excludedDebugLogged = new Set();
    const debugKey = `${pattern.name || pattern.displayMark || pattern.mark}`;
    if (!window.__excludedDebugLogged.has(debugKey)) {
      window.__excludedDebugLogged.add(debugKey);
      console.log(`🧑‍🎓 [除外グループ] ${debugKey}:`, {
        configured_groups: groups,
        resolved_staff_ids: Array.from(ids),
        staffGroups_available: staffGroups.map((g) => g.name),
        pattern_has_configuration: !!pattern?.configuration,
        pattern_keys: Object.keys(pattern || {}),
      });
    }
    return ids;
  };

  // 🔥 当日にそのパターンに配置されているスタッフを「戦力カウント」と「実数」で返す
  //   excluded: カウント対象外（除外グループに属する）スタッフ数
  //   effective: 戦力カウント (total - excluded)
  //   total: 実数（除外含む）
  //   ※ skill_groups.members は employee_id を持つので、employeeId / staff.id どちらにも対応
  const countPatternOnDate = (pattern, date, ids) => {
    const excludedIds = getExcludedStaffIdsForPattern(pattern);
    const mark = pattern.mark || pattern.displayMark;
    let total = 0;
    let excluded = 0;
    (ids || []).forEach((empId) => {
      const value = editableMatrix[`${empId}_${date}`];
      const normalizedValue = value === "" ? "◯" : value;
      if (normalizedValue !== mark) return;
      total += 1;
      if (excludedIds.size > 0) {
        // empId 自体が members に含まれる場合（employee_id 保存パターン）
        if (excludedIds.has(String(empId))) {
          excluded += 1;
          return;
        }
        // staff.id (UUID) が members に含まれる場合（旧フォーマット互換）
        const staff = staffList.find((s) => s.employeeId === empId);
        if (staff && excludedIds.has(String(staff.id))) excluded += 1;
      }
    });
    return { total, effective: total - excluded, excluded };
  };

  // フィルタリングされたスタッフリストを取得
  //   jobTypeFilter が渡された場合（職種別ボタンで自動作成を実行した場合）は
  //   その職種のスタッフだけに絞る
  const getFilteredStaff = () => {
    try {
      let filtered = staffList;
      // 職種フィルタ（自動作成時に指定された職種）
      if (jobTypeFilter) {
        filtered = filtered.filter(
          (s) => String(s.job_type_id || s.jobTypeId || "") === String(jobTypeFilter)
        );
      }

      if (selectedStaffFilter === "auto_assigned") {
        filtered = filtered.filter(s => !s.excludeFromAutoAssign);
      } else if (selectedStaffFilter === "excluded") {
        filtered = filtered.filter(s => s.excludeFromAutoAssign);
      } else if (selectedStaffFilter !== "all") {
        const group = staffGroups.find(g => g.id === selectedStaffFilter);
        if (group && group.members) {
          // members は employee_id ベースが基本だが、旧データの UUID (staff.id) も拾えるよう両対応
          filtered = filtered.filter(staff => group.members.includes(staff.employeeId) || group.members.includes(staff.id));
        }
      }

      return filtered;
    } catch (error) {
      console.error("スタッフフィルタリングエラー:", error);
      return staffList;
    }
  };

  // フィルタリングされた日付の範囲を取得
  const getFilteredDates = () => {
    try {
      if (selectedDateRange === "all") {
        return dates;
      }
      
      if (selectedDateRange === "week1") {
        return dates.filter(date => {
          const day = parseInt(date.split("-")[2]);
          return day <= 7;
        });
      } else if (selectedDateRange === "week2") {
        return dates.filter(date => {
          const day = parseInt(date.split("-")[2]);
          return day > 7 && day <= 14;
        });
      } else if (selectedDateRange === "week3") {
        return dates.filter(date => {
          const day = parseInt(date.split("-")[2]);
          return day > 14 && day <= 21;
        });
      } else if (selectedDateRange === "week4") {
        return dates.filter(date => {
          const day = parseInt(date.split("-")[2]);
          return day > 21;
        });
      }
      
      return dates;
    } catch (error) {
      console.error("日付フィルタリングエラー:", error);
      return dates;
    }
  };

  // シフト競合タブの内容
  const renderConflictsTab = () => {
    try {
      const offConflicts = Object.values(conflicts.off);
      const nightConflicts = Object.values(conflicts.night);
      
      if (offConflicts.length === 0 && nightConflicts.length === 0) {
        return (
          <div>
            <p>希望の重複はありません。すべての希望を適用できました。</p>
          </div>
        );
      }
      
      return (
        <div>
          {offConflicts.length > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <h3>休み希望の重複 ({offConflicts.length}件)</h3>
              {offConflicts.map((conflict, index) => (
                <div key={`off_${index}`} style={{
                  borderBottom: "1px solid #eee",
                  padding: "0.75rem",
                  backgroundColor: "#f9f9f9",
                  marginBottom: "0.75rem",
                  borderRadius: "4px"
                }}>
                  <p>
                    <strong>{conflict.date}</strong>: {conflict.groupName}の休み希望 
                    {conflict.count}名（上限{conflict.limit}名）
                  </p>
                  <div>
                    <p>対象スタッフ:</p>
                    <ul>
                      {conflict.staff.map(empId => {
                        const staff = staffList.find(s => s.employeeId === empId);
                        if (staff?.excludeFromAutoAssign) return null;
                        
                        return (
                          <li key={empId}>
                            {staff ? `${staff.lastName} ${staff.firstName}` : empId}
                            <select
                              value={
                                conflictResolutions[`${conflict.date}_${empId}`] ||
                                editableMatrix[`${empId}_${conflict.date}`] ||
                                ""
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                handleResolveConflict(`${conflict.date}_${empId}`, value);
                                handleShiftChange(empId, conflict.date, value);
                              }}
                              style={{ marginLeft: "1rem" }}
                            >
                              {shiftOptions.map(opt => (
                                <option key={opt} value={opt}>
                                  {opt === "" ? "日勤" : opt}
                                </option>
                              ))}
                            </select>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {nightConflicts.length > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <h3>夜勤希望の重複 ({nightConflicts.length}件)</h3>
              {nightConflicts.map((conflict, index) => (
                <div key={`night_${index}`} style={{
                  borderBottom: "1px solid #eee",
                  padding: "0.75rem",
                  backgroundColor: "#f9f9f9",
                  marginBottom: "0.75rem",
                  borderRadius: "4px"
                }}>
                  <p>
                    <strong>{conflict.date}</strong>: {conflict.groupName}の夜勤希望 
                    {conflict.count}名（上限{conflict.limit}名）
                  </p>
                  <div>
                    <p>対象スタッフ:</p>
                    <ul>
                      {conflict.staff.map(empId => {
                        const staff = staffList.find(s => s.employeeId === empId);
                        if (staff?.excludeFromAutoAssign) return null;
                        
                        return (
                          <li key={empId}>
                            {staff ? `${staff.lastName} ${staff.firstName}` : empId}
                            <select
                              value={
                                conflictResolutions[`${conflict.date}_${empId}`] ||
                                editableMatrix[`${empId}_${conflict.date}`] ||
                                ""
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                handleResolveConflict(`${conflict.date}_${empId}`, value);
                                handleShiftChange(empId, conflict.date, value);
                              }}
                              style={{ marginLeft: "1rem" }}
                            >
                              {shiftOptions.map(opt => (
                                <option key={opt} value={opt}>
                                  {opt === "" ? "日勤" : opt}
                                </option>
                              ))}
                            </select>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    } catch (error) {
      console.error("競合タブレンダリングエラー:", error);
      return <div>競合データの表示中にエラーが発生しました。</div>;
    }
  };

  // シフト表タブの内容
  const renderShiftTableTab = () => {
    // 🔥 デバッグログ追加
    console.log("\n🔍 ShiftAssignmentModal - shiftOptions の中身:");
    console.log("  shiftOptions:", shiftOptions);
    console.log("  shiftOptions.length:", shiftOptions.length);
    
    // 🔥 editableMatrix の夜勤を確認
    const nightInEditable = Object.entries(editableMatrix).filter(([key, value]) => 
      value && (value.includes('[/') || value.includes('ｵC') || value.includes('ｵﾛ'))
    );
    console.log("  editableMatrix の夜勤件数:", nightInEditable.length);
    if (nightInEditable.length > 0) {
      console.log("  editableMatrix 夜勤サンプル:");
      nightInEditable.slice(0, 3).forEach(([key, value]) => {
        console.log(`    ${key}: ${value}`);
      });
    }
    
    try {
      const filteredStaff = getFilteredStaff();
      const filteredDates = getFilteredDates();
      
      return (
        <div>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "1.5rem",
            padding: "0.75rem",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            flexWrap: "wrap"
          }}>
            <div style={{ marginBottom: "0.75rem", marginRight: "1.5rem" }}>
              <label htmlFor="staffFilter">スタッフ: </label>
              <select 
                id="staffFilter"
                value={selectedStaffFilter}
                onChange={(e) => setSelectedStaffFilter(e.target.value)}
                style={{
                  padding: "0.5rem",
                  borderRadius: "4px",
                  border: "1px solid #ddd"
                }}
              >
                <option value="all">すべてのスタッフ</option>
                <option value="auto_assigned">自動割り当て対象のみ</option>
                <option value="excluded">自動割り当て対象外のみ</option>
                <optgroup label="スキルグループ">
                  {staffGroups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            
            <div style={{ marginBottom: "0.75rem", marginRight: "1.5rem" }}>
              <label htmlFor="dateFilter">日付範囲: </label>
              <select 
                id="dateFilter"
                value={selectedDateRange}
                onChange={(e) => setSelectedDateRange(e.target.value)}
                style={{
                  padding: "0.5rem",
                  borderRadius: "4px",
                  border: "1px solid #ddd"
                }}
              >
                <option value="all">全期間</option>
                <option value="week1">第1週 (1-7日)</option>
                <option value="week2">第2週 (8-14日)</option>
                <option value="week3">第3週 (15-21日)</option>
                <option value="week4">第4週 (22日-)</option>
              </select>
            </div>
            
            <div style={{ marginLeft: 'auto', color: '#dc2626', fontWeight: '600' }}>
              最大連勤: {maxConsecutiveWorkDays}日
            </div>
          </div>
          
          {/* 凡例 */}
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            padding: "20px",
            background: "white",
            borderRadius: "16px",
            marginBottom: "24px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(0, 0, 0, 0.05)"
          }}>
            <div style={{fontWeight: "bold", marginRight: "1rem"}}>勤務凡例:</div>
            {Object.entries(shiftColors).map(([type, color]) => (
              <div key={type} style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                borderRadius: "20px",
                background: "rgba(0, 0, 0, 0.02)",
                fontSize: "14px",
                fontWeight: "500"
              }}>
                <div style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "3px",
                  backgroundColor: color,
                  marginRight: "0.3rem"
                }}></div>
                <span>{type === "" ? "日勤" : getPatternLabel(type) || type}</span>
              </div>
            ))}
          </div>
          
          {/* 希望表示の凡例 */}
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            padding: "20px",
            background: "white",
            borderRadius: "16px",
            marginBottom: "24px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(0, 0, 0, 0.05)",
            marginTop: "0.5rem"
          }}>
            <div style={{fontWeight: "bold", marginRight: "1rem"}}>希望表示:</div>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "20px",
              background: "rgba(0, 0, 0, 0.02)",
              fontSize: "14px",
              fontWeight: "500"
            }}>
              <div style={{
                fontSize: "9px",
                fontWeight: "bold",
                padding: "1px 3px",
                borderRadius: "6px",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                border: "1px solid #4CAF50",
                color: "#4CAF50",
                position: "static",
                marginRight: "0.5rem"
              }}>
                休希○
              </div>
              <span>希望適合</span>
            </div>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "20px",
              background: "rgba(0, 0, 0, 0.02)",
              fontSize: "14px",
              fontWeight: "500"
            }}>
              <div style={{
                fontSize: "9px",
                fontWeight: "bold",
                padding: "1px 3px",
                borderRadius: "6px",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                border: "1px solid #ff9800",
                color: "#ff9800",
                position: "static",
                marginRight: "0.5rem"
              }}>
                夜希×
              </div>
              <span>希望不適合</span>
            </div>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "20px",
              background: "rgba(0, 0, 0, 0.02)",
              fontSize: "14px",
              fontWeight: "500"
            }}>
              <div style={{
                fontSize: "9px",
                fontWeight: "bold",
                padding: "1px 3px",
                borderRadius: "6px",
                backgroundColor: 'rgba(33, 150, 243, 0.2)',
                borderColor: '#2196F3',
                color: '#1976D2',
                border: "1px solid #2196F3",
                position: "static",
                marginRight: "0.5rem"
              }}>
                休希○*
              </div>
              <span>管理者設定希望</span>
            </div>
            {/* 🔥 イベントマーカー凡例 */}
            <div style={{display: "flex", alignItems: "center", marginRight: "1rem"}}>
              <div style={{
                width: "10px", height: "10px", borderRadius: "50%",
                background: "#8b5cf6", border: "1px solid white",
                marginRight: "0.5rem", boxShadow: "0 0 0 1px #8b5cf6"
              }} />
              <span>イベント設定あり</span>
            </div>
          </div>

          <div style={{
            overflowX: "auto",
            maxWidth: "100%",
            paddingBottom: "1rem",
            paddingRight: "1rem",
            WebkitOverflowScrolling: "touch"
          }}>
            <div style={{ minWidth: `${filteredDates.length * 85 + 200}px` }}>
              <table style={{
                width: "auto",
                borderCollapse: "collapse",
                tableLayout: "fixed"
              }}>
                <thead>
                  <tr>
                    <th style={{
                      backgroundColor: "#f5f5f5",
                      padding: "0.5rem",
                      border: "1px solid #ddd",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      minWidth: "85px",
                      width: "85px",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      left: 0,
                      backgroundColor: "white",
                      zIndex: 2
                    }}>スタッフ</th>
                    {filteredDates.map(date => {
                      const dayOfWeek = dayjs(date).day();
                      const isSat = dayOfWeek === 6;
                      const isSun = dayOfWeek === 0;
                      const isHolDate = (holidays || []).includes(date);
                      const bg = isSun || isHolDate ? '#fecaca'   // 日・祝: 赤
                        : isSat ? '#bfdbfe'                       // 土: 青
                        : '#f5f5f5';
                      const textColor = isSun || isHolDate ? '#991b1b'
                        : isSat ? '#1e40af'
                        : '#374151';
                      return (
                        <th
                          key={date}
                          style={{
                            backgroundColor: bg,
                            color: textColor,
                            padding: "0.5rem",
                            border: "1px solid #ddd",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            minWidth: "85px",
                            width: "85px",
                            whiteSpace: "nowrap",
                            fontWeight: 700,
                          }}
                        >
                          {dayjs(date).format("D")}
                          <small>({["日","月","火","水","木","金","土"][dayOfWeek]}{isHolDate ? '祝' : ''})</small>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.map(staff => {
                    const isExcluded = staff.excludeFromAutoAssign;
                    const consecutiveInfo = calculateMaxConsecutiveDays(
                      staff.employeeId,
                      dates,
                      editableMatrix,
                      patterns,
                      priorMonthMatrix
                    );
                    
                    const getConsecutiveStyle = () => {
                      if (consecutiveInfo.max > maxConsecutiveWorkDays) {
                        return {
                          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                          color: '#991b1b',
                        };
                      } else if (consecutiveInfo.max >= maxConsecutiveWorkDays - 1) {
                        return {
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          color: '#92400e',
                        };
                      } else {
                        return {
                          background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                          color: '#1e40af',
                        };
                      }
                    };
                    
                    return (
                      <tr key={staff.employeeId} style={{ 
                        borderBottom: "1px solid #ddd",
                        backgroundColor: isExcluded ? "#fff8e1" : "white"
                      }}>
                        <td style={{
                          padding: "0.5rem",
                          border: "1px solid #ddd",
                          textAlign: "center",
                          fontSize: "0.85rem",
                          minWidth: "80px",
                          position: "sticky",
                          left: 0,
                          backgroundColor: "white",
                          zIndex: 2,
                          textAlign: "left",
                          fontWeight: "bold",
                          backgroundColor: isExcluded ? "#fff8e1" : "#f9f9f9"
                        }}>
                          {staff.lastName} {staff.firstName}
                          {isExcluded && (
                            <span style={{
                              display: "inline-block",
                              fontSize: "0.75rem",
                              padding: "0.2rem 0.5rem",
                              borderRadius: "4px",
                              marginLeft: "0.5rem",
                              backgroundColor: "#f59e0b",
                              color: "white"
                            }}>対象外</span>
                          )}
                          
                          {consecutiveInfo.max > 0 && (
                            <div style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "11px",
                              fontWeight: "700",
                              marginTop: "6px",
                              ...getConsecutiveStyle(),
                            }}>
                              <TrendingUp size={10} />
                              <span>最大{Number.isInteger(consecutiveInfo.max) ? consecutiveInfo.max : consecutiveInfo.max.toFixed(1)}連勤</span>
                              {consecutiveInfo.max > maxConsecutiveWorkDays && (
                                <AlertCircle size={10} />
                              )}
                            </div>
                          )}
                        </td>
                        {filteredDates.map((date, dateIndex) => {
                          const key = `${staff.employeeId}_${date}`;
                          const currentValue = editableMatrix[key] === null ? "" : (editableMatrix[key] || "");
                          
                          const isConfirmed = confirmedShifts[key];
                          
                          const hopeStatus = getHopeStatus(staff.employeeId, date);
                          
                          const hope = hopeStatus.hope;
                          const hopeType = hopeStatus.type;
                          let hopeDisplay = "";
                          let hopeStyle = {};
                          let isHopeMatch = false;
                          
                          if (hope) {
                            const baseLabel = getHopeLabel(hope, patterns);
                            isHopeMatch = checkHopeMatch(hope, currentValue, patterns);
                            
                            let matchSymbol = "";
                            if (isHopeMatch === true) {
                              matchSymbol = "○";
                              hopeStyle = {
                                borderColor: "#4CAF50",
                                color: "#4CAF50",
                              };
                            } else if (isHopeMatch === false) {
                              matchSymbol = "×";
                              hopeStyle = {
                                borderColor: "#ff9800",
                                color: "#ff9800",
                              };
                            } else {
                              matchSymbol = "-";
                              hopeStyle = {
                                borderColor: "#2196F3",
                                color: "#2196F3",
                              };
                            }
                            
                            hopeDisplay = `${baseLabel}${matchSymbol}`;
                            
                            if (hopeType === 'admin') {
                              hopeStyle = {
                                ...hopeStyle,
                                backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                borderColor: '#2196F3',
                                fontWeight: 'bold'
                              };
                            }
                          }
                          
                          const dateIndex2 = dates.indexOf(date);
                          const isMaxConsecutive = dateIndex2 >= consecutiveInfo.start &&
                                                   dateIndex2 <= consecutiveInfo.end &&
                                                   consecutiveInfo.max > maxConsecutiveWorkDays;

                          // 🔥 土日祝の判定（祝日リストは prop で渡される）
                          const dow = dayjs(date).day();
                          const isSat = dow === 6;
                          const isSun = dow === 0;
                          const isHolDate = (holidays || []).includes(date);
                          const isWeekendOrHoliday = isSat || isSun || isHolDate;

                          // bgColor の優先順:
                          //   違反 (赤) > シフトマーク色 > 土日祝 (薄色) > 白
                          let bgColor = shiftColors[currentValue]
                            || (isSun || isHolDate ? '#fee2e2'   // 日曜・祝日: 薄赤
                              : isSat ? '#dbeafe'                 // 土曜: 薄青
                              : '#ffffff');

                          if (isMaxConsecutive) {
                            bgColor = "#fee2e2";
                          }

                          const cellStyle = {
                            padding: "0.5rem",
                            border: "1px solid #ddd",
                            textAlign: "center",
                            fontSize: "0.85rem",
                            minWidth: "80px",
                            backgroundColor: isExcluded ?
                              `${bgColor}90` :
                              bgColor,
                            // 土日祝の左帯アクセント（シフトマーク色を上書きしてもわかるように）
                            ...(isWeekendOrHoliday && !isMaxConsecutive ? {
                              boxShadow: (isSun || isHolDate)
                                ? 'inset 3px 0 0 #dc2626'
                                : 'inset 3px 0 0 #2563eb'
                            } : {}),
                            ...(isConfirmed ? {
                              border: "2px solid #4CAF50",
                              position: "relative"
                            } : {}),
                            ...(isMaxConsecutive ? { boxShadow: "0 0 0 2px rgba(239, 68, 68, 0.3) inset" } : {}),
                            ...(hope ? { position: 'relative' } : {}),
                          };
                          
                          return (
                            <td key={key} style={cellStyle}>
                              {isMaxConsecutive && (
                                <div style={{
                                  position: 'absolute',
                                  top: '2px',
                                  left: '2px',
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  backgroundColor: '#ef4444',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <TrendingUp size={10} color="white" />
                                </div>
                              )}
                              
                              {isConfirmed && (
                                <div style={{
                                  position: "absolute",
                                  top: "2px",
                                  right: "2px",
                                  width: "10px",
                                  height: "10px",
                                  borderRadius: "50%",
                                  backgroundColor: "#4CAF50"
                                }} title="確定済み"></div>
                              )}
                              
                              {hope && (
                                <div style={{
                                  position: "absolute",
                                  bottom: "2px",
                                  right: "2px",
                                  fontSize: "9px",
                                  fontWeight: "bold",
                                  padding: "1px 3px",
                                  borderRadius: "6px",
                                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                                  border: "1px solid",
                                  zIndex: 1,
                                  minWidth: "18px",
                                  textAlign: "center",
                                  display: 'block',
                                  visibility: 'visible',
                                  opacity: 1,
                                  zIndex: 10,
                                  ...hopeStyle,
                                }} title={`${hopeType === 'admin' ? '管理者設定' : '個人提出'}希望: ${getHopeLabel(hope)} ${isHopeMatch === true ? '(適合)' : isHopeMatch === false ? '(不適合)' : '(日勤希望)'}`}>
                                  {hopeDisplay}
                                  {hopeType === 'admin' && (
                                    <span style={{ fontSize: '8px', marginLeft: '1px' }}>*</span>
                                  )}
                                </div>
                              )}
                              
                              <select
                                value={currentValue}
                                onChange={(e) => handleShiftChange(staff.employeeId, date, e.target.value)}
                                style={{ 
                                  width: "100%", 
                                  minWidth: "70px",
                                  fontSize: "0.9rem"
                                }}
                              >
                                {/* 🔥 デバッグ用：currentValue が選択肢に含まれているか確認 */}
 
                                {shiftOptions.map(opt => (
                                  <option key={opt} value={opt}>
                                    {opt === "" ? "日勤" : opt}
                                  </option>
                                ))}
                              </select>

                              {hopeType === 'admin' && (
                                <div style={{
                                  position: "absolute",
                                  top: "2px",
                                  right: "2px",
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  background: "#16a34a",
                                  border: "1px solid white"
                                }} />
                              )}
                              {/* 🔥 イベントマーカー (紫): リカーリングイベントが該当するセル */}
                              {events[`${staff.employeeId}_${date}`] && (
                                <div
                                  title={`イベント: ${events[`${staff.employeeId}_${date}`]}`}
                                  style={{
                                    position: "absolute",
                                    top: "2px",
                                    left: "2px",
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    background: "#8b5cf6",
                                    border: "1px solid white"
                                  }}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    } catch (error) {
      console.error("シフト表タブレンダリングエラー:", error);
      return <div>シフト表の表示中にエラーが発生しました。</div>;
    }
  };

  // 🔥 修正版：概要タブの内容（改善された表示）
  const renderOverviewTab = () => {
    try {
      const stats = calculateShiftStats();
      const hopeStats = calculateHopeReflectionRate();
      
      // 🔥 新規追加：グループ化されたパターンデータを取得
      const staffIds = staffList.map(s => s.employeeId);
      const groupedPatterns = groupPatternsByGroupName(patterns, editableMatrix, staffIds, dates);
      
      // 日ごとのシフト集計を作成（グループベース）
      const dailyGroupStats = {};
      dates.forEach(date => {
        dailyGroupStats[date] = {};
        Object.keys(groupedPatterns).forEach(groupName => {
          dailyGroupStats[date][groupName] = 0;
        });
      });
      
      // 日ごとのグループ集計を計算
      staffList.forEach(staff => {
        dates.forEach(date => {
          const key = `${staff.employeeId}_${date}`;
          const value = editableMatrix[key] || "";
          const normalizedValue = value === "" ? "◯" : value;
          
          // どのグループに属するかを判定
          Object.entries(groupedPatterns).forEach(([groupName, groupData]) => {
            const belongsToGroup = groupData.patterns.some(pattern => 
              pattern.mark === normalizedValue || 
              (pattern.mark === "◯" && value === "")
            );
            
            if (belongsToGroup) {
              dailyGroupStats[date][groupName]++;
            }
          });
        });
      });
      
      const excludedCount = excludedStaff.length;
      
      // 🔥 残課題サマリ: 固定 / 柔軟 / 連勤 / チェーン を統合表示
      const fixedConflicts = conflicts?.fixedShortage || [];
      const fixedShortageCount =
        conflicts?.fixedShortageCount
        ?? fixedConflicts.length
        ?? 0;
      const fixedUnderCount = fixedConflicts.filter((s) => (s.diff ?? (s.have - s.required)) < 0).length;
      const fixedOverCount = fixedConflicts.filter((s) => (s.diff ?? (s.have - s.required)) > 0).length;
      const flexShortageCount = (conflicts?.flexShortage || []).length;
      const consecutiveViolations = conflicts?.consecutive || [];
      // 同一 (empId, start) の最長 run だけ残す
      const dedupedConsec = (() => {
        const m = new Map();
        consecutiveViolations.forEach((c) => {
          const k = `${c.empId}_${c.start}`;
          if (!m.has(k) || m.get(k).length < c.length) m.set(k, c);
        });
        return Array.from(m.values()).sort((a, b) => b.length - a.length);
      })();
      const consecutiveCount = dedupedConsec.length;
      // 🔥 同一マーク連続違反・後続禁止違反も総計に含める
      const sameConsecViolations = conflicts?.sameConsecutive || [];
      const forbiddenPostViolations = conflicts?.forbiddenPost || [];
      const sameConsecCount = sameConsecViolations.length;
      const forbiddenPostCount = forbiddenPostViolations.length;
      const totalIssues = fixedShortageCount + flexShortageCount + consecutiveCount
        + sameConsecCount + forbiddenPostCount;
      const isComplete = totalIssues === 0;

      return (
        <div>
          {/* ❗ 完成 / 未完バナー */}
          {!isComplete && (
            <div
              style={{
                backgroundColor: "#fee2e2",
                border: "2px solid #dc2626",
                borderRadius: "8px",
                padding: "16px 20px",
                marginBottom: "16px",
                color: "#7f1d1d",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                <AlertCircle size={24} color="#dc2626" />
                <h3 style={{ margin: 0, color: "#7f1d1d", fontWeight: 800 }}>
                  ⚠️ シフト作成 未完了 — 残課題 {totalIssues} 件
                </h3>
              </div>
              {/* 🔥 課題カテゴリ別サマリ */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px', fontSize: '12px' }}>
                {fixedUnderCount > 0 && (
                  <span style={{ background: '#fecaca', border: '1px solid #f87171', padding: '4px 10px', borderRadius: '12px', fontWeight: 700 }}>
                    📉 固定人数 不足 {fixedUnderCount} 件
                  </span>
                )}
                {fixedOverCount > 0 && (
                  <span style={{ background: '#fed7aa', border: '1px solid #fb923c', padding: '4px 10px', borderRadius: '12px', fontWeight: 700 }}>
                    📈 固定人数 過剰 {fixedOverCount} 件
                  </span>
                )}
                {consecutiveCount > 0 && (
                  <span style={{ background: '#fde68a', border: '1px solid #f59e0b', padding: '4px 10px', borderRadius: '12px', fontWeight: 700 }}>
                    🔄 連勤違反 {consecutiveCount} 件
                  </span>
                )}
                {flexShortageCount > 0 && (
                  <span style={{ background: '#bfdbfe', border: '1px solid #60a5fa', padding: '4px 10px', borderRadius: '12px', fontWeight: 700 }}>
                    📋 柔軟最低未達 {flexShortageCount} 件
                  </span>
                )}
                {sameConsecCount > 0 && (
                  <span style={{ background: '#fbcfe8', border: '1px solid #ec4899', padding: '4px 10px', borderRadius: '12px', fontWeight: 700 }}>
                    🔁 同一マーク連続 {sameConsecCount} 件
                  </span>
                )}
                {forbiddenPostCount > 0 && (
                  <span style={{ background: '#ddd6fe', border: '1px solid #8b5cf6', padding: '4px 10px', borderRadius: '12px', fontWeight: 700 }}>
                    🚫 後続禁止 {forbiddenPostCount} 件
                  </span>
                )}
              </div>

              {/* 同一マーク連続違反リスト */}
              {sameConsecCount > 0 && (
                <details style={{ marginTop: '4px', marginBottom: '8px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#9d174d' }}>
                    🔁 同一マーク連続違反 {sameConsecCount} 件の詳細
                  </summary>
                  <div style={{ marginTop: '6px', fontSize: '12px', maxHeight: '160px', overflowY: 'auto', background: '#fdf2f8', padding: '8px', borderRadius: '6px' }}>
                    {sameConsecViolations.slice(0, 50).map((c, i) => {
                      const staff = staffList.find((s) => s.employeeId === c.empId);
                      const name = staff ? `${staff.lastName || ''} ${staff.firstName || ''}`.trim() : c.empId;
                      return (
                        <div key={i} style={{ padding: '3px 0', color: '#831843' }}>
                          <strong>{name}</strong>: {c.start} 〜 {c.end} の {c.patternName}({c.mark}) <strong>{c.length}連続</strong>（上限 {c.limit}）
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}

              {/* 後続禁止違反リスト */}
              {forbiddenPostCount > 0 && (
                <details style={{ marginTop: '4px', marginBottom: '8px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#5b21b6' }}>
                    🚫 後続禁止違反 {forbiddenPostCount} 件の詳細
                  </summary>
                  <div style={{ marginTop: '6px', fontSize: '12px', maxHeight: '160px', overflowY: 'auto', background: '#f5f3ff', padding: '8px', borderRadius: '6px' }}>
                    {forbiddenPostViolations.slice(0, 50).map((c, i) => {
                      const staff = staffList.find((s) => s.employeeId === c.empId);
                      const name = staff ? `${staff.lastName || ''} ${staff.firstName || ''}`.trim() : c.empId;
                      return (
                        <div key={i} style={{ padding: '3px 0', color: '#5b21b6' }}>
                          <strong>{name}</strong>: {c.date} の {c.patternName}({c.mark}) → 翌 {c.nextDate} に <strong>{c.nextMark}</strong>（禁止組み合わせ）
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
              {/* 連勤違反リスト */}
              {consecutiveCount > 0 && (
                <details style={{ marginTop: '4px', marginBottom: '8px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#92400e' }}>
                    🔄 連勤違反 {consecutiveCount} 件の詳細
                  </summary>
                  <div style={{ marginTop: '6px', fontSize: '12px', maxHeight: '200px', overflowY: 'auto', background: '#fffbeb', padding: '8px', borderRadius: '6px' }}>
                    {dedupedConsec.slice(0, 50).map((c, i) => {
                      const staff = staffList.find((s) => s.employeeId === c.empId);
                      const name = staff ? `${staff.lastName || ''} ${staff.firstName || ''}`.trim() : c.empId;
                      return (
                        <div key={i} style={{ padding: '3px 0', color: '#78350f' }}>
                          <strong>{name}</strong>: {c.start} 〜 {c.end} の <strong>{Number(c.length).toFixed(1)}連勤</strong>
                        </div>
                      );
                    })}
                    {dedupedConsec.length > 50 && (
                      <div style={{ color: '#92400e', fontStyle: 'italic' }}>…他 {dedupedConsec.length - 50} 件</div>
                    )}
                  </div>
                </details>
              )}
              <p style={{ margin: "0 0 8px 0", fontSize: "13px", lineHeight: "1.6" }}>
                <strong>そのまま保存しても完成シフトとして扱わないでください</strong>。考えられる原因:
              </p>
              <ul style={{ margin: "4px 0 8px 20px", fontSize: "13px", lineHeight: "1.6" }}>
                <li>スキルグループのメンバー不足（管理画面でメンバー追加）</li>
                <li>希望が固定人数と矛盾（事前検証ログを確認）</li>
                <li>連勤上限・職種制約で候補が枯渇（設定見直し）</li>
              </ul>
              <details style={{ marginTop: "4px" }} open>
                <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                  未達の詳細と希望調整候補（{Math.min(fixedShortageCount, 50)} 件まで表示）
                </summary>
                <div style={{ marginTop: "8px", fontSize: "12px", maxHeight: "360px", overflowY: "auto" }}>
                  {(conflicts?.fixedShortage || []).slice(0, 50).map((s, i) => {
                    const diff = s.diff ?? (s.have - s.required);
                    const isOver = diff > 0;
                    const badge = isOver ? '📈 過剰' : '📉 不足';
                    const badgeColor = isOver ? '#c2410c' : '#dc2626';
                    const badgeBg = isOver ? '#fed7aa' : '#fecaca';
                    return (
                    <div key={i} style={{ padding: "8px 0", borderBottom: "1px dashed #fca5a5" }}>
                      <div style={{ fontWeight: 600 }}>
                        <span style={{
                          background: badgeBg, color: badgeColor, padding: '2px 6px',
                          borderRadius: '8px', fontSize: '11px', marginRight: '6px', fontWeight: 700,
                        }}>{badge} {Math.abs(diff)}人</span>
                        {s.date} の {s.patternName}({s.mark}) が <strong>{s.have}/{s.required}人</strong>
                        {s.reasons && (
                          <span style={{ color: "#991b1b", marginLeft: "8px", fontSize: "11px", fontWeight: 400 }}>
                            [skillGroup:{s.reasons.skillGroup} / consec:{s.reasons.consecutive} / monthly:{s.reasons.monthlyLimit} / sameConsec:{s.reasons.sameConsec}]
                          </span>
                        )}
                      </div>
                      {s.hopeAdjusters && s.hopeAdjusters.length > 0 ? (
                        <div style={{ marginTop: "4px", paddingLeft: "12px", color: "#7c2d12" }}>
                          🛠 希望調整候補 <strong>{s.hopeAdjusters.length}名</strong>（このうち誰かが希望を取り下げれば穴埋め可能）:
                          <div style={{ marginTop: "4px", display: "flex", flexWrap: "wrap", gap: "4px" }}>
                            {s.hopeAdjusters.map((a, j) => (
                              <span
                                key={j}
                                style={{
                                  background: "#fef3c7",
                                  border: "1px solid #fde68a",
                                  borderRadius: "12px",
                                  padding: "2px 8px",
                                  fontSize: "11px",
                                  color: "#78350f",
                                }}
                                title={`職員番号: ${a.empId}`}
                              >
                                {a.name}（{a.hopeLabel}）
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginTop: "4px", paddingLeft: "12px", color: "#6b7280", fontStyle: "italic" }}>
                          🛠 この日に休み/有給/半休系の希望を出している該当職種スタッフはいません。
                          <br />→ <strong>必要人数設定の見直し</strong> もしくは <strong>スキルグループ/職種制約の確認</strong> が必要です。
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </details>
            </div>
          )}
          {isComplete && (
            <div
              style={{
                backgroundColor: "#dcfce7",
                border: "2px solid #16a34a",
                borderRadius: "8px",
                padding: "12px 16px",
                marginBottom: "16px",
                color: "#14532d",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div style={{ fontSize: "20px" }}>✅</div>
              <strong>シフト作成 完了 — 固定人数 100% 達成</strong>
            </div>
          )}

          {/* 希望反映率の表示 */}
          <div style={{
            ...styles.statsCard,
            backgroundColor: "#e3f2fd",
            border: "1px solid #2196F3"
          }}>
            <h3>希望反映率</h3>
            <div style={styles.statGrid}>
  <div style={styles.statItem}>
    <div style={styles.statValue}>{hopeStats.total.rate}%</div>
    <div>全体反映率</div>
    <div style={{fontSize: "0.85rem", color: "#666"}}>
      {hopeStats.total.reflected}/{hopeStats.total.count}件
    </div>
  </div>
  
  {/* 動的に希望タイプを表示 */}
  {Object.entries(hopeStats.byType)
    .filter(([_, data]) => data.count > 0)  // 0件は表示しない
    .sort((a, b) => b[1].count - a[1].count)  // 件数が多い順
    .map(([type, data]) => {
      // 色を動的に決定
      let color = "#607d8b";
      if (type === "off" || type === "OFF") color = "#4CAF50";
      else if (type === "night" || type === "/") color = "#ff9800";
      else if (type === "none" || type === "day" || type === "◯") color = "#2196F3";
      else if (type.startsWith("[/")) color = "#9c27b0";
      
      return (
        <div key={type} style={styles.statItem}>
          <div style={{...styles.statValue, color}}>
            {data.rate}%
          </div>
          <div>{data.label}</div>
          <div style={{fontSize: "0.85rem", color: "#666"}}>
            {data.reflected}/{data.count}件
          </div>
        </div>
      );
    })}
  
  {hopeStats.admin.count > 0 && (
    <div style={styles.statItem}>
      <div style={{...styles.statValue, color: "#1976D2"}}>
        {hopeStats.admin.rate}%
      </div>
      <div>管理者設定希望</div>
      <div style={{fontSize: "0.85rem", color: "#666"}}>
        {hopeStats.admin.reflected}/{hopeStats.admin.count}件
      </div>
    </div>
  )}
</div>
              {hopeStats.admin.count > 0 && (
                <div style={styles.statItem}>
                  <div style={{...styles.statValue, color: "#1976D2"}}>
                    {hopeStats.admin.rate}%
                  </div>
                  <div>管理者設定希望</div>
                  <div style={{fontSize: "0.85rem", color: "#666"}}>
                    {hopeStats.admin.reflected}/{hopeStats.admin.count}件
                  </div>
                </div>
              )}
            </div>
          
          
          {/* 自動割り当て状況のアラート */}
          {excludedCount > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #f59e0b',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              boxShadow: '0 4px 15px rgba(245, 158, 11, 0.2)',
            }}>
              <strong>自動割り当て対象外のスタッフ: {excludedCount}名</strong>
              <p style={{ margin: "0.5rem 0" }}>
                これらのスタッフは固定シフトパターンが適用され、自動割り当て処理から除外されました。
              </p>
              <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem" }}>
                {excludedStaff.map(staff => (
                  <li key={staff.employeeId}>
                    {staff.lastName} {staff.firstName}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* 🔥 修正版：グループごとの勤務タイプ分布（改善された表示） */}
          <div style={styles.statsCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>日ごとの勤務タイプ分布（グループ表示）</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#6b7280' }}>
                  <input
                    type="checkbox"
                    checked={useGroupDisplay}
                    onChange={(e) => setUseGroupDisplay(e.target.checked)}
                    style={{ marginRight: '4px' }}
                  />
                  グループ表示
                </label>
                
                {/* グループソート方法選択 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>並び順:</span>
                  <select
                    value={groupSortMethod}
                    onChange={(e) => setGroupSortMethod(e.target.value)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '12px',
                      background: 'white',
                      color: '#374151',
                    }}
                  >
                    <option value="alphabetical">あいうえお順</option>
                    <option value="frequency">使用頻度順</option>
                    <option value="custom">カスタム順</option>
                  </select>
                </div>
                
                <button
                  onClick={() => {
                    const allExpanded = Object.values(showGroupDetails).every(v => v);
                    const newState = {};
                    Object.keys(groupedPatterns).forEach(group => {
                      newState[group] = !allExpanded;
                    });
                    setShowGroupDetails(newState);
                  }}
                  style={{
                    ...styles.toggleButton,
                    background: Object.values(showGroupDetails).some(v => v) ? '#e0f7fa' : 'white',
                    color: Object.values(showGroupDetails).some(v => v) ? '#0891b2' : '#374151',
                    borderColor: Object.values(showGroupDetails).some(v => v) ? '#0891b2' : '#d1d5db',
                  }}
                >
                  {Object.values(showGroupDetails).every(v => v) ? <EyeOff size={12} /> : <Eye size={12} />}
                  {Object.values(showGroupDetails).every(v => v) ? '全て閉じる' : '全て展開'}
                </button>
              </div>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                fontSize: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      color: '#374151', 
                      fontWeight: '700',
                      border: '1px solid #e2e8f0',
                      minWidth: '120px'
                    }}>
                      {useGroupDisplay ? 'シフトグループ' : 'シフト種別'}
                    </th>
                    {dates.map((date) => {
                      const dayOfWeek = dayjs(date).day();
                      const dayName = ["日","月","火","水","木","金","土"][dayOfWeek];
                      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                      
                      return (
                        <th key={date} style={{ 
                          padding: '8px 4px', 
                          textAlign: 'center', 
                          color: '#374151',
                          fontWeight: '600',
                          minWidth: '45px',
                          border: '1px solid #e2e8f0',
                          backgroundColor: isWeekend ? '#fef3c7' : '#f8fafc',
                          fontSize: '11px'
                        }}>
                          <div>{dayjs(date).date()}</div>
                          <div style={{ 
                            fontSize: '9px', 
                            color: isWeekend ? '#92400e' : '#6b7280',
                            fontWeight: '500'
                          }}>
                            ({dayName})
                          </div>
                        </th>
                      );
                    })}
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'center', 
                      color: '#374151',
                      fontWeight: '700',
                      minWidth: '60px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#f1f5f9'
                    }}>
                      合計
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* 🔥 グループ表示 */}
                  {useGroupDisplay && Object.entries(groupedPatterns)
                    .sort(([nameA, dataA], [nameB, dataB]) => {
                      switch (groupSortMethod) {
                        case "frequency":
                          return dataB.totalCount - dataA.totalCount;
                        case "custom":
                          // カスタム順序：日勤系 → 夜勤系 → その他はあいうえお順
                          const customOrder = ["日勤系", "夜勤系", "オンコール系", "遅番系", "休日系"];
                          const indexA = customOrder.indexOf(nameA);
                          const indexB = customOrder.indexOf(nameB);
                          
                          if (indexA !== -1 && indexB !== -1) {
                            return indexA - indexB;
                          } else if (indexA !== -1) {
                            return -1;
                          } else if (indexB !== -1) {
                            return 1;
                          } else {
                            return nameA.localeCompare(nameB, 'ja', { numeric: true });
                          }
                        case "alphabetical":
                        default:
                          return nameA.localeCompare(nameB, 'ja', { numeric: true });
                      }
                    })
                    .map(([groupName, groupData]) => {
                      const isExpanded = showGroupDetails[groupName];
                      // 🔥 月合計の effective / total / excluded をパターン単位で計算
                      let totalEffective = 0, totalAll = 0, totalExcluded = 0;
                      dates.forEach((date) => {
                        groupData.patterns.forEach((p) => {
                          const c = countPatternOnDate(p, date, staffIds);
                          totalEffective += c.effective;
                          totalAll += c.total;
                          totalExcluded += c.excluded;
                        });
                      });
                      const totalCount = totalEffective; // 戦力カウント
                      // グループ単位の合計 required
                      const groupRequiredByDate = (date) =>
                        groupData.patterns.reduce(
                          (s, p) => s + (getRequiredCountForPattern(p, date) || 0),
                          0
                        );
                      const totalRequired = dates.reduce(
                        (sum, date) => sum + groupRequiredByDate(date),
                        0
                      );
                      
                      return (
                        <React.Fragment key={groupName}>
                          {/* グループ行 */}
                          <tr style={{
                            ...styles.groupRow,
                            borderLeftColor: groupData.color,
                            background: groupData.color,
                          }}>
                            <td style={{ 
                              padding: '12px 8px', 
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              border: '1px solid #e2e8f0',
                              minHeight: '48px'
                            }}>
                              <button
                                onClick={() => setShowGroupDetails(prev => ({
                                  ...prev,
                                  [groupName]: !prev[groupName]
                                }))}
                                style={styles.groupToggle}
                              >
                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </button>
                              <div style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                background: '#2c3e50',
                                flexShrink: 0
                              }} />
                              <span style={{ 
                                fontWeight: '700',
                                fontSize: '13px',
                                color: '#1f2937',
                                whiteSpace: 'nowrap'
                              }}>
                                {groupName}
                              </span>
                              {groupData.isIndividual && (
                                <span style={{ 
                                  fontSize: '9px', 
                                  color: '#f59e0b',
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  padding: '2px 6px',
                                  borderRadius: '6px',
                                  fontWeight: '600'
                                }}>
                                  個別
                                </span>
                              )}
                              <span style={{ 
                                fontSize: '9px', 
                                color: '#6b7280',
                                backgroundColor: 'rgba(255,255,255,0.8)',
                                padding: '2px 6px',
                                borderRadius: '6px',
                                marginLeft: 'auto',
                                fontWeight: '500'
                              }}>
                                {groupData.patterns.length}種類
                              </span>
                            </td>
                            {dates.map((date) => {
                              const required = groupRequiredByDate(date);
                              const showFraction = required > 0;
                              // 🔥 グループ内の全パターンを集計（effective / total / excluded）
                              let total = 0, effective = 0, excluded = 0;
                              groupData.patterns.forEach((p) => {
                                const c = countPatternOnDate(p, date, staffIds);
                                total += c.total;
                                effective += c.effective;
                                excluded += c.excluded;
                              });
                              const assigned = effective; // 戦力カウント
                              const unsatisfied = showFraction && assigned < required;
                              const label = showFraction
                                ? (excluded > 0 ? `${effective}(${total})/${required}` : `${effective}/${required}`)
                                : (excluded > 0 ? `${effective}(${total})` : `${effective}`);
                              return (
                                <td key={`${groupName}_${date}`} style={{
                                  padding: '12px 4px',
                                  textAlign: 'center',
                                  fontWeight: '700',
                                  color: unsatisfied ? '#b91c1c' : '#1f2937',
                                  fontSize: '13px',
                                  border: '1px solid #e2e8f0',
                                  background: unsatisfied ? 'rgba(254,226,226,0.6)' : undefined,
                                }}
                                title={excluded > 0
                                  ? `戦力 ${effective}名 / 必要 ${required}名（除外 ${excluded}名含めて実数 ${total}名）`
                                  : `${effective}/${required}名`}>
                                  {label}
                                </td>
                              );
                            })}
                            <td style={{
                              padding: '12px 8px',
                              textAlign: 'center',
                              fontWeight: '800',
                              color: '#1f2937',
                              background: 'rgba(255,255,255,0.9)',
                              borderLeft: '3px solid #2c3e50',
                              border: '1px solid #e2e8f0',
                              fontSize: '14px'
                            }}
                            title={totalExcluded > 0
                              ? `戦力 ${totalEffective}名 / 必要 ${totalRequired}名（除外 ${totalExcluded}名含めて実数 ${totalAll}名）`
                              : `合計 ${totalCount}/${totalRequired}`}>
                              {totalRequired > 0
                                ? (totalExcluded > 0
                                    ? `${totalEffective}(${totalAll})/${totalRequired}`
                                    : `${totalEffective}/${totalRequired}`)
                                : (totalExcluded > 0 ? `${totalEffective}(${totalAll})` : totalCount)}
                            </td>
                          </tr>
                          
                          {/* 詳細行（展開時） */}
                          {isExpanded && groupData.patterns.map(pattern => {
                            // 🔥 パターン月合計: effective / total / excluded
                            let pEffective = 0, pTotal = 0, pExcluded = 0;
                            dates.forEach((date) => {
                              const c = countPatternOnDate(pattern, date, staffIds);
                              pEffective += c.effective;
                              pTotal += c.total;
                              pExcluded += c.excluded;
                            });
                            const patternTotalCount = pEffective;

                            return (
                              <tr key={`${groupName}_${pattern.id}_detail`} style={{
                                ...styles.detailRow,
                                borderLeft: '4px solid ' + groupData.color
                              }}>
                                <td style={{ 
                                  padding: '8px 8px 8px 48px', 
                                  fontWeight: '500', 
                                  color: '#6c757d',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  border: '1px solid #e2e8f0',
                                  fontSize: '11px'
                                }}>
                                  <div style={{ width: '2px', height: '12px', background: '#6c757d' }} />
                                  <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#6c757d',
                                    flexShrink: 0
                                  }} />
                                  <span style={{ whiteSpace: 'nowrap' }}>{pattern.name}</span>
                                  <span style={{ 
                                    fontSize: '8px', 
                                    color: '#9ca3af',
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    padding: '1px 4px',
                                    borderRadius: '4px',
                                    marginLeft: 'auto'
                                  }}>
                                    {pattern.mark}
                                  </span>
                                </td>
                                {dates.map((date) => {
                                  // 🔥 戦力カウント / 実数 / 除外
                                  const { total, effective, excluded } = countPatternOnDate(pattern, date, staffIds);
                                  const dayCount = effective;
                                  const required = getRequiredCountForPattern(pattern, date);
                                  const fixed = isFixedPattern(pattern);
                                  const showFraction = required > 0;
                                  const unsatisfied = fixed && showFraction && dayCount !== required;
                                  const overflow = showFraction && dayCount > required;
                                  const label = showFraction
                                    ? (excluded > 0 ? `${effective}(${total})/${required}` : `${effective}/${required}`)
                                    : (excluded > 0 ? `${effective}(${total})` : effective);
                                  return (
                                    <td key={`${pattern.id}_${date}`} style={{
                                      padding: '8px 4px',
                                      textAlign: 'center',
                                      fontWeight: unsatisfied ? '800' : '500',
                                      color: unsatisfied ? '#dc2626' : overflow && fixed ? '#dc2626' : '#6c757d',
                                      background: unsatisfied ? '#fee2e2' : 'transparent',
                                      fontSize: '10px',
                                      border: unsatisfied ? '1px solid #dc2626' : '1px solid #e2e8f0',
                                    }}
                                    title={
                                      excluded > 0
                                        ? `${pattern.name}: 戦力 ${effective}名 (除外 ${excluded}名含めて実数 ${total}名) / 必要 ${required}名${fixed ? '（固定）' : '（柔軟）'}`
                                        : (showFraction
                                            ? `${pattern.name}: ${dayCount}/${required}人${fixed ? '（固定）' : '（柔軟最低）'}`
                                            : `${pattern.name}: ${dayCount}人`)
                                    }>
                                      {label}
                                    </td>
                                  );
                                })}
                                <td style={{ 
                                  padding: '8px', 
                                  textAlign: 'center', 
                                  fontWeight: '600',
                                  color: '#6c757d',
                                  fontSize: '11px',
                                  borderLeft: '2px solid #6c757d',
                                  border: '1px solid #e2e8f0'
                                }}
                                title={pExcluded > 0
                                  ? `戦力 ${pEffective}名（除外 ${pExcluded}名含めて実数 ${pTotal}名）`
                                  : `${patternTotalCount}名`}>
                                  {pExcluded > 0 ? `${pEffective}(${pTotal})` : patternTotalCount}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  
                  {/* 🔥 個別表示（従来） */}
                  {!useGroupDisplay && Object.entries(stats.byPattern).map(([shiftType, data]) => {
                    const totalCount = dates.reduce((sum, date) => {
                      return sum + staffIds.filter(staffId => {
                        const value = editableMatrix[`${staffId}_${date}`];
                        const normalizedValue = value === "" ? "◯" : value;
                        return normalizedValue === shiftType;
                      }).length;
                    }, 0);
                    
                    return (
                      <tr key={shiftType} style={{ 
                        background: shiftColors[shiftType] || '#f9fafb',
                        transition: 'all 0.2s ease-in-out'
                      }}>
                        <td style={{ 
                          padding: '12px 8px', 
                          fontWeight: '600', 
                          color: '#374151',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: '#374151',
                            flexShrink: 0
                          }} />
                          <span style={{ whiteSpace: 'nowrap' }}>{data.label}</span>
                        </td>
                        {dates.map((date) => {
                          const dayCount = staffIds.filter(staffId => {
                            const value = editableMatrix[`${staffId}_${date}`];
                            const normalizedValue = value === "" ? "◯" : value;
                            return normalizedValue === shiftType;
                          }).length;
                          
                          return (
                            <td key={`${shiftType}_${date}`} style={{ 
                              padding: '12px 4px', 
                              textAlign: 'center', 
                              fontWeight: '600',
                              color: '#374151',
                              border: '1px solid #e2e8f0'
                            }}>
                              {dayCount}
                            </td>
                          );
                        })}
                        <td style={{ 
                          padding: '12px 8px', 
                          textAlign: 'center', 
                          fontWeight: '700',
                          color: '#374151',
                          borderLeft: '3px solid #374151',
                          border: '1px solid #e2e8f0'
                        }}>
                          {totalCount}
                        </td>
                      </tr>
                    );
                  })}
                  
                  {/* 総合計行 */}
                  <tr style={{ 
                    background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                    color: 'white',
                    borderTop: '3px solid #374151'
                  }}>
                    <td style={{ 
                      padding: '12px 8px', 
                      fontWeight: '800', 
                      fontSize: '13px',
                      border: '1px solid #374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>📊 総計</span>
                    </td>
                    {dates.map((date) => {
                      const dayTotal = staffIds.length;
                      return (
                        <td key={`total_${date}`} style={{ 
                          padding: '12px 4px', 
                          textAlign: 'center', 
                          fontWeight: '800',
                          fontSize: '13px',
                          border: '1px solid #374151'
                        }}>
                          {dayTotal}
                        </td>
                      );
                    })}
                    <td style={{ 
                      padding: '12px 8px', 
                      textAlign: 'center', 
                      fontWeight: '900',
                      fontSize: '15px',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      border: '1px solid #374151',
                      borderLeft: '3px solid #dc2626'
                    }}>
                      {staffIds.length * dates.length}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    } catch (error) {
      console.error("概要タブレンダリングエラー:", error);
      return <div>概要データの表示中にエラーが発生しました。</div>;
    }
  };

  // 勤務パターンタブの内容
  const renderPatternDetailsTab = () => {
    try {
      return (
        <div>
          <h3>勤務パターン一覧</h3>
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            padding: "20px",
            background: "white",
            borderRadius: "16px",
            marginBottom: "24px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(0, 0, 0, 0.05)"
          }}>
            {patterns.map(pattern => {
              const mark = pattern.displayMark || pattern.id;
              const bgColor = shiftColors[mark] || "#f5f5f5";
              
              return (
                <div key={pattern.id} style={{
                  display: "inline-block",
                  padding: "0.25rem 0.5rem",
                  margin: "0.1rem",
                  borderRadius: "4px",
                  fontSize: "0.8rem",
                  color: "#333",
                  backgroundColor: bgColor,
                  border: "1px solid #ddd",
                  marginBottom: "0.5rem"
                }}>
                  {pattern.name}
                  {pattern.displayMark && <span> ({pattern.displayMark})</span>}
                </div>
              );
            })}
          </div>
          
          {patterns.map(pattern => (
            <div key={pattern.id} style={{
              marginBottom: "2rem",
              padding: "1rem",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #eee",
              borderLeft: `4px solid ${shiftColors[pattern.displayMark || pattern.id] || "#ddd"}`
            }}>
              <h4>{pattern.name}</h4>
              <p><strong>表示記号:</strong> {pattern.displayMark || "なし"}</p>
              
              <p><strong>勤務時間:</strong> {pattern.startTime || "--:--"} 〜 {pattern.endTime || "--:--"}</p>
              
              <p>
                <strong>優先度:</strong> {pattern.priority || "未設定"}
                {pattern.forceAssign && (
                  <span style={{
                    marginLeft: "0.5rem",
                    color: "#e65100",
                    fontWeight: "bold"
                  }}>
                    ★ 強制割り当て有効
                  </span>
                )}
              </p>
              
              <p>
                <strong>人数設定:</strong> 
                <span style={{
                  display: "inline-block",
                  padding: "0.25rem 0.5rem",
                  marginLeft: "0.5rem",
                  backgroundColor: pattern.flexibilityType === "fixed" ? "#e3f2fd" : "#e8f5e9",
                  borderRadius: "4px",
                  fontSize: "0.85rem"
                }}>
                  {pattern.flexibilityType === "fixed" ? "固定人数" : 
                  `柔軟人数 (最低${pattern.minRequired || 0}人)`}
                </span>
              </p>
              
              {pattern.groupName && (
                <p><strong>グループ:</strong> {pattern.groupName}</p>
              )}
              
              {pattern.targetGroup && (
                <p>
                  <strong>対象グループ:</strong> {
                    staffGroups.find(g => g.id === pattern.targetGroup)?.name || pattern.targetGroup
                  }
                </p>
              )}
              
              {pattern.requiredCount && Object.keys(pattern.requiredCount).length > 0 && (
                <div>
                  <p><strong>曜日別必要人数:</strong></p>
                  <div style={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap"
                  }}>
                    {["月", "火", "水", "木", "金", "土", "日"].map(day => (
                      <div key={day} style={{
                        padding: "0.5rem",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        minWidth: "60px",
                        textAlign: "center"
                      }}>
                        <div style={{ fontWeight: "bold" }}>{day}</div>
                        <div>{pattern.requiredCount[day] || 0}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {pattern.postShiftIds && pattern.postShiftIds.length > 0 && (
                <div style={{ marginTop: "1rem" }}>
                  <p><strong>後続シフト:</strong></p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                    {pattern.postShiftIds.map(id => {
                      const postPattern = patterns.find(p => p.id === id);
                      const color = postPattern ? 
                        shiftColors[postPattern.displayMark || postPattern.id] : "#f5f5f5";
                      
                      return postPattern ? (
                        <div key={id} style={{
                          display: "inline-block",
                          padding: "0.25rem 0.5rem",
                          margin: "0.1rem",
                          borderRadius: "4px",
                          fontSize: "0.8rem",
                          color: "#333",
                          backgroundColor: color
                        }}>
                          {postPattern.name}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              {pattern.isChainOnlyPattern && (
                <p style={{ marginTop: "0.5rem", fontWeight: "bold", color: "#9C27B0" }}>
                  後続シフト専用パターン
                </p>
              )}
            </div>
          ))}
        </div>
      );
    } catch (error) {
      console.error("パターン詳細タブレンダリングエラー:", error);
      return <div>勤務パターンの表示中にエラーが発生しました。</div>;
    }
  };

  // モーダルが開いていない場合は何も表示しない
  if (!isOpen) return null;

  // ローディング中表示
  if (loading) {
    return (
      <div style={styles.modalBackdrop} onClick={onClose}>
        <div style={{
          ...styles.modalContent,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            border: "4px solid rgba(0, 0, 0, 0.1)",
            borderLeft: "4px solid #3498db",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            animation: "spin 1s linear infinite",
            marginBottom: "1rem"
          }}></div>
          <h3>データ読み込み中...</h3>
          <p>シフト情報を準備しています</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2>シフト割り当て確認</h2>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <div style={styles.tabNav}>
          <button 
            style={{
              ...styles.tabButton, 
              ...(activeTab === "overview" ? styles.tabButtonActive : {})
            }}
            onClick={() => setActiveTab("overview")}
          >
            概要
          </button>
          <button 
            style={{
              ...styles.tabButton, 
              ...(activeTab === "conflicts" ? styles.tabButtonActive : {})
            }}
            onClick={() => setActiveTab("conflicts")}
          >
            競合 ({Object.keys(conflicts.off).length + Object.keys(conflicts.night).length})
          </button>
          <button 
            style={{
              ...styles.tabButton, 
              ...(activeTab === "shiftTable" ? styles.tabButtonActive : {})
            }}
            onClick={() => setActiveTab("shiftTable")}
          >
            シフト表
          </button>
          <button 
            style={{
              ...styles.tabButton, 
              ...(activeTab === "patterns" ? styles.tabButtonActive : {})
            }}
            onClick={() => setActiveTab("patterns")}
          >
            勤務パターン
          </button>
        </div>
        
        <div>
          {activeTab === "overview" && renderOverviewTab()}
          {activeTab === "conflicts" && renderConflictsTab()}
          {activeTab === "shiftTable" && renderShiftTableTab()}
          {activeTab === "patterns" && renderPatternDetailsTab()}
        </div>
        
        <button style={styles.saveButton} onClick={handleSave}>
          変更を保存して適用
        </button>
      </div>
      
      {/* CSSアニメーション */}
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ShiftAssignmentModal;